import { resolveProgressiveLoadEnabled, TASKLIST_LOAD_STATUS } from './tasklistProgressiveLoad.js';

// Shortcut prefix for Masters menu items
export const MASTER_PREFIX = 'Master';

/**
 * Tasklist progressive load (NXTGEN-8043 F4). Page 1 renders first, the rest append.
 * Build with VUE_APP_FC_TASKLIST_PROGRESSIVE_LOAD=false for one full getchecklist request.
 */
export const TASKLIST_PROGRESSIVE_LOAD = Object.freeze({
  ENABLED: resolveProgressiveLoadEnabled(process.env.VUE_APP_FC_TASKLIST_PROGRESSIVE_LOAD, true),
  FIRST_PAGE_SIZE: 100,
  PAGE_SIZE: 200,
  CONCURRENCY: 3,
});

export { TASKLIST_LOAD_STATUS };

export const TASKLIST_LOAD_MESSAGES = Object.freeze({
  progress: (loaded, total) => `Loading ${loaded} of ${total}`,
  PAGE_FAILED: 'Some tasks could not be loaded.',
  RETRY: 'Retry',
  ACTIONS_LOCKED: 'Available when all tasks have loaded',
});

/**
 * Trial balance progressive load. Page 1 renders first. Remaining offsets
 * are requested together and appended in offset order.
 */
export const TB_PROGRESSIVE_LOAD = Object.freeze({
  FIRST_PAGE_SIZE: 2000,
  PAGE_SIZE: 5000,
});

export const TB_LOAD_MESSAGES = Object.freeze({
  progress: (loaded, total) => `Loading ${loaded} of ${total}`,
  PAGE_FAILED: 'Some trial balance rows could not be loaded.',
  RETRY: 'Retry',
  ACTIONS_LOCKED: 'Download is available when all rows have loaded',
});

/**
 * Server-paged trial balance. Default on.
 * Build with VUE_APP_FC_TB_PAGINATED=false to keep the full in-browser TrialBalance.vue.
 * getdetails still uses page "tb" (TB_DETAILS_PAGE). VIEW_PAGE is the saved-view key.
 */
export const TB_DETAILS_PAGE = 'tb';

export const TB_PAGINATED = Object.freeze({
  ENABLED: resolveProgressiveLoadEnabled(process.env.VUE_APP_FC_TB_PAGINATED, true),
  PAGE_SIZE: 50,
  VIEW_PAGE: 'fc_trialbalance',
  EXPORT_FILENAME: 'Trial Balance.xlsx',
});

/** Column keys the paginated grid sends as filter and sort fields. Location label is overridden on the page. */
export const TB_PAGINATED_FIELDS = Object.freeze([
  Object.freeze({ key: 'glcode', label: 'Account No.', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'name', label: 'Account Name', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'reference_detail', label: 'Tasklist Reference', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'location', label: 'Location', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'type', label: 'Type', type: 'string', filterable: true, sortable: true }),
  // Server exact and not_equals on these amounts use tolerance ±1. Comparison operators do not.
  Object.freeze({ key: 'opening_amount', label: 'Opening Amount', type: 'number', filterable: true, sortable: true }),
  Object.freeze({ key: 'transaction', label: 'Transaction', type: 'number', filterable: true, sortable: true }),
  Object.freeze({ key: 'audit_entries', label: 'Journal Entries', type: 'number', filterable: true, sortable: true }),
  Object.freeze({ key: 'closing_amount', label: 'Closing Amount', type: 'number', filterable: true, sortable: true }),
  Object.freeze({ key: 'category', label: 'Category', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'subcategory', label: 'Sub Category', type: 'string', filterable: true, sortable: true }),
  Object.freeze({ key: 'created_at', label: 'Created At', type: 'date', filterable: true, sortable: true }),
  Object.freeze({ key: 'updated_at', label: 'Updated At', type: 'date', filterable: true, sortable: true }),
]);

export const TB_PAGINATED_MESSAGES = Object.freeze({
  SAVED: 'View saved',
  SAVE_FAILED: 'Failed to save view',
  FETCH_META_FAILED: 'Failed to fetch trial balance data',
  FETCH_PAGE_FAILED: 'Failed to fetch trial balance',
  EXPORT_FAILED: 'Failed to download trial balance',
  EMPTY: 'No accounts match the current filters.',
  LIST_STILL_LOADING: 'Available when the list has finished loading',
  NO_COLUMN_CHANGES: 'No column changes to save',
  NO_RECORDS_TO_EXPORT: 'No records to export',
  EXPORT_IN_PROGRESS: 'Export in progress',
});

/** tbUpload.stage from getdetails, bulkaddtb, and tbUploadStatus. */
export const TB_UPLOAD_STAGE = Object.freeze({
  QUEUED: 1,
  IN_PROGRESS: 2,
  FINISHED: 3,
  FAILED: 4,
});

export const TB_UPLOAD = Object.freeze({
  POLL_INTERVAL_MS: 5000,
  PROCESSING_STATUS_QUEUED: 'queued',
  HTTP_ACCEPTED: 202,
  HTTP_CONFLICT: 409,
});

export const TB_UPLOAD_MESSAGES = Object.freeze({
  COMPLETED: 'Trial balance upload completed',
  FAILED: 'Trial balance upload failed',
  ALREADY_RUNNING: 'A trial balance upload is already in progress for this cycle. Please wait for it to finish.',
  START_FAILED: 'Failed to start trial balance upload',
  STATUS_FAILED: 'Failed to check trial balance upload status',
  IN_PROGRESS_PREFIX: 'Trial balance upload in progress by',
  UNKNOWN_STARTER: 'another user',
  BUTTON_IN_PROGRESS: 'Upload is in progress',
});

/**
 * Cycle stage values.
 * These map to the `stage` field on project/cycle documents.
 */
export const CYCLE_STAGES = {
  OPEN: 0,
  PENDING_REVIEW: 1,
  CLOSED: 3,
};

/**
 * Labels for cycle stage values (used in UI display).
 */
export const STAGE_LABELS = {
  [0]: 'Open',
  [1]: 'Pending for review',
  [3]: 'Closed',
};

/** Cycle stage text stored on the project (`stage_text`). */
export const CYCLE_STAGE_TEXT = { 0: 'open', 3: 'closed' };

export const UPSI_STAGES = { UNPUBLISHED: 0, PENDING_APPROVAL: 1, PUBLISHED: 2, REJECTED: 3 };
export const UPSI_STAGE_LABELS = { 0: 'Unpublished', 1: 'Awaiting Approval', 2: 'Published', 3: 'Rejected' };
export const UPSI_DECISIONS = { APPROVE: 'approve', REJECT: 'reject' };
export const UPSI_LIMITS = { FILING_REFERENCE_MAX: 256, REMARKS_MAX: 1000 };

export const UPSI_TOOLTIPS = {
  VIEW_DETAILS: 'View request details',
  REOPEN: 'Reopen cycle',
  REOPEN_BLOCKED_PENDING:
    'This cycle has a publish request waiting for approval. The request must be rejected before the cycle can be reopened.',
  REVIEW: 'Review request to publish',
};

/** Server `review_block_reason` values the cycle list branches on. */
export const UPSI_REVIEW_BLOCK_REASON = {
  OWN_REQUEST: 'own_request',
};

/** Review is hidden unless the user can review. These strings are not shown on a disabled Review button. */
export const UPSI_REVIEW_BLOCK_TOOLTIPS = {
  not_manager: 'Only a Controller who manages this cycle can approve or reject this request.',
  not_controller: 'Only a Controller who manages this cycle can approve or reject this request.',
  no_right: "You don't have permission to approve or reject publish requests.",
};

export const UPSI_HISTORY_VERBS = {
  requested: 'requested to publish',
  approved: 'approved the request',
  rejected: 'rejected the request',
  withdrawn: 'withdrew the request',
  cycle_reopened: 'reopened the cycle',
  cancelled: 'cancelled the request',
  backfilled: 'updated the record',
};

export const UPSI_MESSAGES = {
  DIALOG_TITLE: 'UPSI Compliance',
  DIALOG_TITLE_REQUEST: 'UPSI Compliance',
  DIALOG_TITLE_REPUBLISH: 'UPSI Compliance',
  DIALOG_TITLE_REVIEW: 'UPSI Compliance',
  DETAILS_SUFFIX: { 0: 'Not requested yet', 1: 'Awaiting approval', 2: 'Approved', 3: 'Rejected' },
  BANNER_REQUEST: 'Publishing sends this cycle to a Controller for approval.',
  BANNER_REPUBLISH: 'Publishing sends this cycle back to a Controller for approval.',
  BANNER_REVIEW: 'Approving publishes this cycle. Rejecting sends it back with your remarks.',
  BANNER_UNPUBLISHED: 'This cycle has not been published yet. Request details appear here after a publish request is raised.',
  BANNER_AWAITING: 'Waiting for a Controller to approve or reject this request.',
  BANNER_APPROVED: '{reviewer} approved this request. The cycle is now Published.',
  BANNER_REJECTED: '{reviewer} rejected this request. A manager with access to every location can correct the details and publish again.',
  TIME_HELP_REQUEST: "The date and time are recorded automatically by the system when you press Publish. You can't edit them.",
  TIME_HELP_REPUBLISH: 'Recorded when you last published. They update automatically when you publish again.',
  FILING_REFERENCE_REQUIRED: 'Enter a filing reference.',
  REJECT_REMARKS_REQUIRED: 'Add remarks to reject this request. They are shared with {requester}.',
  CONTROLLER_REMARKS: 'Controller remarks',
  PENDING_REVIEW: 'Pending review',
  REOPEN_WARNING_AWAITING:
    'This cycle has a publish request waiting for approval. Reopening it changes its UPSI status to Unpublished.',
  REOPEN_WARNING_PUBLISHED:
    'This cycle is Published. Reopening it changes its UPSI status to Unpublished. After you close it again, it must be published and approved again.',
  REOPEN_WARNING_REJECTED:
    'This cycle has a rejected publish request. Reopening it changes its UPSI status to Unpublished. The rejection stays in the request history.',
};

export const FC_UPSI_API = {
  REQUEST: (id) => `/v3/financialclose/upsi/request/${id}`,
  REVIEW: (id) => `/v3/financialclose/upsi/review/${id}`,
  DETAILS: (id) => `/v3/financialclose/upsi/details/${id}`,
};

/**
 * Financial Close route path helpers and prefixes.
 */
export const FC_ROUTES = {
  HOME: '/financialclose/home',
  HOMEVIEW: '/financialclose/homeview',
  SUPPORT: '/financialclose/support',
  PROJECT_PREFIX: '/financialclose/project/',
  CHECKLIST: (id) => `/financialclose/project/checklist/${id}`,
  DASHBOARD: (id) => `/financialclose/project/dashboard/${id}`,
  CYCLE_SETTINGS: (id) => `/financialclose/project/cycle-settings/${id}`,
  CYCLE_SETTINGS_TEAM: (id) => `/financialclose/project/cycle-settings/${id}/team`,
  CYCLE_SETTINGS_THRESHOLD: (id) => `/financialclose/project/cycle-settings/${id}/threshold`,
  CYCLE_SETTINGS_AUTOMATIONS: (id) => `/financialclose/project/cycle-settings/${id}/automations`,
  USERS: (id) => `/financialclose/project/users/${id}`,
  THRESHOLD: (id) => `/financialclose/project/threshold/${id}`,
  SETUP_LOCATION: '/financialclose/setup/location',
  SETUP_TASKLIST: '/financialclose/setup/tasklist',
  SETUP_TASK_DETAIL: (id) => `/financialclose/setup/tasklist/${id}`,
  SETUP_USERS: '/financialclose/setup/users',
  SETUP_CHART_OF_ACCOUNT: '/financialclose/setup/chartofaccount',
  SETUP_INFORMATION_MANAGER: '/financialclose/setup/informationmanager',
  SETUP_EMAIL_NOTIFICATIONS: '/financialclose/setup/emailnotifications',
  /** Path infix for FS Preparation (cycle context, not setup masters) */
  FS_PREPARATION_PATH_INFIX: '/financialstatementpreparation/',
  MANAGE_FS_PREPARATION_PREFIX: '/financialclose/manage/financialstatementpreparation/',
};

/**
 * Regex to detect undefined project IDs in financial close routes.
 * Matches any /financialclose/…/undefined path segment, covering nested
 * routes such as /financialclose/automation/riskmanagementhome/:id,
 * /financialclose/manage/financialstatementpreparation/:id, etc.
 */
export const FC_UNDEFINED_ID_PATTERN = /\/financialclose\/(?:[^/]+\/)+undefined(?:\?|$|\/)/;

/** Mongo ObjectId path segment (cycle / project ids). */
export const MONGO_OBJECT_ID_PATTERN = '[a-f0-9]{24}';

/**
 * Page-first FBC project routes: `/financialclose/project/{slug}/:cycleId`.
 * Distinct from ID-first variance: `/financialclose/project/:cycleId/varianceanalysis/...`.
 */
export const FBC_PAGE_FIRST_SLUGS = [
  'checklist',
  'dashboard',
  'trialbalance',
  'cycle-settings',
  'users',
  'threshold',
  'informationmanager',
  'informationrequests',
  'varianceanalysis',
];

/** Non-project FBC routes that still carry a cycle ObjectId. */
export const FBC_OTHER_CYCLE_PREFIX_PATTERN =
  'manage/financialstatementpreparation|functionalsupport|automation[^/]*';

/**
 * Menu item name constants used across FC components.
 */
export const FC_MENU_NAMES = {
  MASTERS: 'Masters',
  SHORTCUTS: 'Shortcuts',
  CYCLES: 'Cycles',
  BOLT_ON_MODULES: 'Bolt On Modules',
  CYCLE_SETTINGS: 'Cycle Settings',
  TASKLIST: 'Tasklist',
  LOCATION: 'Location',
  CHART_OF_ACCOUNTS: 'Chart of Accounts',
  INFORMATION_MANAGER: 'Information Manager',
  THRESHOLD: 'Threshold',
  EMAIL_NOTIFICATIONS: 'Automations',
  ACCRUALS: 'Accruals',
};

/** Legacy API menu names still returned by some cycle payloads */
export const FC_MENU_LEGACY_NAMES = {
  ARC_AGENT: 'Arc Agent',
};

/** Cycle side-nav labels (API name → display label) */
export const FC_CYCLE_NAV_DISPLAY_NAMES = {
  [FC_MENU_NAMES.TASKLIST]: 'Tasks',
  [FC_MENU_NAMES.INFORMATION_MANAGER]: 'Information Request',
};

/** Settings / Masters side-nav labels (API name → display label) */
export const FC_SETTINGS_NAV_DISPLAY_NAMES = {
  [FC_MENU_NAMES.TASKLIST]: 'Task Library',
  [FC_MENU_NAMES.EMAIL_NOTIFICATIONS]: 'Automations',
};

/** FBC nav icon paths (no spaces in filenames) */
export const FC_NAV_ICONS = {
  COLLAPSE_NAV: '/img/navicon/fbc-nav/NavCollapse.svg',
  ACCRUALS: '/img/navicon/fbc-nav/Accruals.svg',
  CYCLE_SETTING: '/img/navicon/CycleSetting.svg',
  LOCATION: '/img/navicon/Location.svg',
  CHART_OF_ACCOUNTS: '/img/navicon/ChartOfAccounts.svg',
  TASK_LIBRARY: '/img/navicon/TaskLibrary.svg',
  INFO_MANAGER: '/img/navicon/InfoManager.svg',
  THRESHOLD: '/img/navicon/Threshold.svg',
  EMAIL_NOTIFICATION: '/img/navicon/EmailNotification.svg',
  AUTOMATION_AGENT: '/img/navicon/AutomationAgent.svg',
};

/** Icon fallbacks when Masters store items ship with empty `icon` */
export const FC_SETTINGS_ICON_BY_NAME = {
  [FC_MENU_NAMES.LOCATION]: FC_NAV_ICONS.LOCATION,
  [FC_MENU_NAMES.CHART_OF_ACCOUNTS]: FC_NAV_ICONS.CHART_OF_ACCOUNTS,
  [FC_MENU_NAMES.TASKLIST]: FC_NAV_ICONS.TASK_LIBRARY,
  [FC_MENU_NAMES.INFORMATION_MANAGER]: FC_NAV_ICONS.INFO_MANAGER,
  [FC_MENU_NAMES.THRESHOLD]: FC_NAV_ICONS.THRESHOLD,
  [FC_MENU_NAMES.EMAIL_NOTIFICATIONS]: FC_NAV_ICONS.EMAIL_NOTIFICATION,
};

/** Abbreviated month names for cycle date labels */
export const FC_MONTH_ABBREV = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * @param {string} apiName
 * @returns {string}
 */
export function getCycleNavDisplayName(apiName) {
  return FC_CYCLE_NAV_DISPLAY_NAMES[apiName] || apiName;
}

/**
 * @param {string} apiName
 * @returns {string}
 */
export function getSettingsNavDisplayName(apiName) {
  return FC_SETTINGS_NAV_DISPLAY_NAMES[apiName] || apiName;
}

/**
 * API endpoints for the financialclose project store.
 */
export const FC_PROJECT_API = {
  GET_V3: '/v3/financialclose/analysis/getprojects',
  GET_V3_LITE: '/v3/financialclose/analysis/getprojects?view=lite',
  GET_FORM_DATA: '/v2/financialclose/project/getformdata/',
  ADD: '/v2/financialclose/project/add',
  EDIT: (id) => `/v2/financialclose/project/edit/${id}`,
  DELETE: (id) => `/v2/financialclose/project/delete/${id}`,
  CLOSE: (id) => `/v2/financialclose/project/close/${id}`,
  OPEN: (id) => `/v2/financialclose/project/open/${id}`,
  DISABLE_NOTIFICATION: (id) => `/v2/financialclose/project/disablenotification/${id}`,
  ENABLE_NOTIFICATION: (id) => `/v2/financialclose/project/enablenotification/${id}`,
  GENERATE_AI_CHECKLIST: '/v2/financialclose/project/generateaichecklist',
  RETRY_CYCLE_CREATION: (id) => `/v2/financialclose/project/retrycyclecreation/${id}`,
  GET_DASHBOARD: (id) => `/v2/financialclose/analysis/getdashboard/${id}`,
  CHART_OF_ACCOUNTS_GET: '/v2/financialclose/masters/chartofaccount/get',
  ERP_SYNC_TRIAL_BALANCE: 'v2/erp/sync-trialbalance',
};

/** Cycle Settings (team + threshold) API endpoints */
export const FC_CYCLE_SETTINGS_API = {
  THRESHOLD_GET: (cycleId) => `/v2/financialclose/project/threshold/get/${cycleId}`,
  THRESHOLD_ADD: (cycleId) => `/v2/financialclose/project/threshold/add/${cycleId}`,
  THRESHOLD_EDIT: (thresholdId) => `v2/financialclose/project/threshold/edit/${thresholdId}`,
  GET_USER_DETAILS: (cycleId) => `/v2/financialclose/analysis/getuserdetails/${cycleId}`,
  GET_USERS_FORMDATA: '/v2/financialclose/analysis/getusersformdata',
  MANAGE_INFO_OWNER: (pbcProjectId) => `/v2/financialclose/project/manageinfoowner/${pbcProjectId}`,
  LOCATION_GET: '/v2/financialclose/location/get',
};

/**
 * Cycle creation status values for BookClosure projects.
 */
export const CYCLE_CREATION_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  PARTIALLY_COMPLETED: 'partially_completed',
  FAILED: 'failed',
};

/**
 * Generates the activity log API URL for a given resource ID.
 * @param {string} id - The resource _id
 * @returns {string} The full activity log API endpoint URL
 */
export const activityLogUrl = (id) => `/v2/activitylogs/get/${id}`;

/** Cycles-page feed. cycleId is matched by listByCycle, not as activitylogs reference. */
export const activityLogCycleUrl = (cycleId) =>
  `/v2/financialclose/project/activitylogs/cycle/${cycleId}`;

export const CYCLE_MESSAGES = {
  SUCCESS: {
    NEW_CYCLE_CREATED: "New cycle created",
    PROJECT_UPDATED: "Project Updated",
    PROJECT_CLOSED: "Project Closed",
    PROJECT_REOPENED: "Project Re-opened",
    EMAIL_NOTIFICATION_DISABLED: "Email Notifications Disabled",
    EMAIL_NOTIFICATION_ENABLED: "Email Notifications Enabled",
    EMAIL_NOTIFICATION_SUCCESS: type => `${type} successfully`,
    STATUS_CHANGED: 'Status changed',
    CYCLE_CREATION_RETRY: 'Cycle setup retry initiated',
    CYCLE_DELETED: 'Cycle deleted successfully',
  },
  ERROR: {
    UNKNOWN: "Unknown error!",
    SELECT_POPULATE_CHECKLIST: "Select Populate checklist",
    ERROR_CLOSING_PROJECT: "Error closing project",
    ERROR_REOPENING_PROJECT: "Error re-opening project",
    ERROR_GETTING_USER_INFO: "Error getting the user information",
    PLEASE_SELECT_START_DATE: "Please select start date",
    PLEASE_SELECT_END_DATE: "Please select end date",
    PLEASE_ENTER_DESCRIPTION: "Please enter description",
    PLEASE_SELECT_CYCLE_TYPE: "Please select a cycle type",
    PLEASE_SELECT_CYCLE_LOCATIONS: "Please select at least one location",
    NO_TAGS_SELECTED: "Please select at least one tag",
    NO_PROJECT_SELECTED: "Please select a project",
    UNSUPPORTED_FILE_EXTENSION: "Only xlsx,xls,xlsm,xlsb file extension supported",
    ERROR_CHANGING_STAGE: 'Error changing stage',
    // Shown only when the server message is missing. Prefer the server text when present.
    CYCLE_ALREADY_CLOSED: "This cycle was already closed by another user. The list has been refreshed.",
    CYCLE_ALREADY_OPEN: "This cycle was already reopened by another user. The list has been refreshed.",
  },
  PROJECT: {
    CHECKLIST_MASTER: 'checklistmaster',
    BLANK: 'blank',
    PREVIOUS_PROJECT: 'previousproject',
  }
};

/** Populate-checklist / template source values (API `checklistfrom`). */
export const CHECKLIST_FROM = {
  CHECKLIST_MASTER: CYCLE_MESSAGES.PROJECT.CHECKLIST_MASTER,
  BLANK: CYCLE_MESSAGES.PROJECT.BLANK,
  PREVIOUS_PROJECT: CYCLE_MESSAGES.PROJECT.PREVIOUS_PROJECT,
};

/** Sentinel for "Select all" in Location multi-select. */
export const LOCATION_SELECT_ALL = {
  ID: '__select_all__',
  LABEL: 'Select all',
};

// Status and change status text for Information Requests landing
export const STATUS_TEXT = {
  's-1': { text: 'Cancelled', color: 'error' },
  's0': { text: 'Pending Submission', color: 'info' },
  's1': { text: 'Pending Approval', color: 'warning' },
  's2': { text: 'Approved', color: 'success' }
};

export const CHANGE_STATUS_TEXT = {
  'pending': { text: 'Pending', stages: [2, -1], changeto: 0, color: 'info' },
  'approve': { text: 'Approve', stages: [1], changeto: 2, color: 'success' },
  'close': { text: 'Close', stages: [0], changeto: 2, color: 'success' },
  'cancel': { text: 'Cancel', stages: [0, 1, 2], changeto: -1, color: 'error' },
};

/**
 * Numeric limit (200) shared by multiple due-date flows — semantics depend on the consumer.
 *
 * **Cycle / close checklist (working days, cycle-anchored)** — pass as `maxWorkingDays` to
 * `getCycleDueDateWindow(cycleEnd, MAX_DUE_DATE_DAYS)` in `utils/workingDays.js`. Selectable
 * range is anchor ± this many **working days** (Sat/Sun excluded). Anchor is cycle end when
 * `metadata.to` is set; otherwise today. Used by task list edit/add, bulk due date, Horizon
 * bulk update, `validateDueDateRange`, and aligned backend validation.
 *
 * **Information Manager requests (calendar days from today)** — `InformationRequests/modals/
 * AddEditRequestModal.vue` uses this value as a **calendar-day** cap only (`today +
 * MAX_DUE_DATE_DAYS`). That flow is not cycle-based; do not route it through
 * `getCycleDueDateWindow` without an explicit product change.
 *
 * For signed Due Days on templates/imports, see `DUE_DAYS_MIN` / `DUE_DAYS_MAX` (−200…+200).
 */
export const MAX_DUE_DATE_DAYS = 200;

/** Due-date range error copy. Cycle tasks use working days from cycle end, not calendar days from today. */
export const DUE_DATE_RANGE_ERROR = {
  FROM_TODAY: (maxDays) => `Due Date must be within ${maxDays} working days before or after today`,
  FROM_CYCLE_END: (maxDays) =>
    `Due Date must be within ${maxDays} working days before or after the cycle end date`,
};

/** Signed due-days offset accepted on Information Manager create/edit and bulk upload */
export const DUE_DAYS_MIN = -200;
export const DUE_DAYS_MAX = 200;

/** API field keys for due-day errors shown on the Due Days input. */
export const DUE_FIELD_KEYS = ['duedays', 'duedate'];

/** Display labels for enabled/disabled status in tables and exports. */
export const ENABLE_DISABLE_STATUS_LABELS = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

/**
 * @param {boolean|undefined} enabled
 * @returns {string}
 */
export function formatEnableDisableStatus(enabled) {
  return enabled
    ? ENABLE_DISABLE_STATUS_LABELS.ENABLED
    : ENABLE_DISABLE_STATUS_LABELS.DISABLED;
}

/**
 * Normalize notification enabled/disabled status from API or UI values.
 * @param {*} status
 * @param {*} flag
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export function parseNotificationFlag(status, flag, defaultValue = false) {
  if (typeof status === 'string') {
    const normalized = status.trim().toLowerCase();
    if (normalized === 'enabled' || normalized === 'enable' || normalized === 'true') return true;
    if (normalized === 'disabled' || normalized === 'disable' || normalized === 'false') return false;
  }
  if (typeof flag === 'boolean') return flag;
  if (typeof status === 'boolean') return status;
  return defaultValue;
}

/** Individual activity log event type keys. */
export const ACTIVITY_LOG_EVENT = {
  MASTER_TASK_CREATED: 'master_task_created',
};

/** Activity log event types used by activity-log component. */
export const ACTIVITY_LOG_TYPES = {
  TASKLIST: ["cycle_task_created","cycle_task_deleted","cycle_task_bulk_created","cycle_bulk_task_updated", "project_create", "projectcreate", "project_update", "projectupdate"],
  TRIAL_BALANCE: ['cycle_tb_add'],
  TASKLIST_IM: [
    "cycle_bulk_create_information_manager",
    "cycle_information_manager_added",
    "cycle_information_manager_deleted",
    "pbc_info_owner_created",
    "pbc_info_owner_updated",
    "pbc_info_owner_deleted",
  ],
  USERS: ['userassociate', 'usermoved', 'userreplaced', 'userremoved'],
  CYCLE_THRESHOLD: [
    'cycle_threshold_added',
    'cycle_threshold_updated',
    'cycle_threshold_enabled',
    'cycle_threshold_disabled',
  ],
  MASTER_CHART_OF_ACCOUNT: ["master_chart_of_account_add","master_chart_of_account_bulkadd"],
  MASTER_TASKLIST: [
    ACTIVITY_LOG_EVENT.MASTER_TASK_CREATED,
    "master_bulk_task_created",
    "master_bulk_task_updated",
    "master_add_custom_column",
  ],
  MASTER_IM:["master_create_information_manager","master_bulk_create_information_manager"],
  MASTER_LOCATION: [
    'master_location_add',
    'master_location_update',
    'master_location_enable',
    'master_location_disable',
  ],
  /** Lifecycle event types. The cycles-row feed does not send this list; it loads the full cycle match. */
  CYCLE: [
    'projectcreate',
    'project_create',
    'projectupdate',
    'project_update',
    'projectupdated',
    'projectclose',
    'projectopen',
    'notificationenable',
    'notificationdisable',
    'projectdelete',
  ],
};

/** Display labels for Information Requests activity log type filters. */
export const INFORMATION_REQUESTS_ACTIVITY_LOG_CONFIG = {
  cycle_bulk_create_information_manager: { displaytext: 'Bulk Information Request Added' },
  cycle_information_manager_added: { displaytext: 'Information Request Created' },
  cycle_information_manager_deleted: { displaytext: 'Request Deleted' },
  pbc_info_owner_created: { displaytext: 'Information Owner Created' },
  pbc_info_owner_updated: { displaytext: 'Information Owner Updated' },
  pbc_info_owner_deleted: { displaytext: 'Information Owner Deleted' },
};

/** Activity log fetch / validation messages. */
export const ACTIVITY_LOG_MESSAGES = {
  RESOURCE_OR_MODULE_REQUIRED: 'resourceId or moduleName is required',
};

/**
 * Cycle Export Manager (NXTGEN-7411).
 * POST accepts the job. GET lists queue rows for this cycle and this user.
 * GET `/:exportId/file` returns a signed URL (`data.url`), which the dialog opens.
 * Queue stages: 1 queued, 2 in progress, 3 finished, 4 failed.
 */
export const ACTIVITY_LOG_EXPORT = {
  URL: '/v2/export',
  SCOPE_CYCLE: 'cycle',
  EXPORT_TYPE_ACTIVITY_LOG: 'activity_log',
  EXPORT_TYPE_FILE: 'file',
  PROCESSING_STATUS_INITIATED: 'initiated',
  STAGE_QUEUED: 1,
  STAGE_IN_PROGRESS: 2,
  STAGE_FINISHED: 3,
  STAGE_FAILED: 4,
  POLL_MS: 5000,
  DATE_FORMAT: 'MMM D, YYYY, h:mm A',
  LABEL_ACTIVITY_LOG: 'Activity logs export',
  LABEL_FILE: 'Task files attachment export',
  STATUS_GENERATING: 'Generating',
  STATUS_READY: 'Ready',
  STATUS_FAILED: 'Failed',
  ACTION_PENDING: '-',
  FILE_FALLBACK_NAME: 'activity-log-export.xlsx',
  HISTORY_HEADERS: [
    {
      value: 'exportTypeLabel',
      text: 'Export type',
      visible: true,
      filterable: true,
      sortable: false,
    },
    {
      value: 'requestedAt',
      text: 'Requested on',
      visible: true,
      filterable: true,
      sortable: false,
      type: 'date-range',
    },
    {
      value: 'statusLabel',
      text: 'Status',
      visible: true,
      filterable: true,
      sortable: false,
    },
    {
      value: 'action',
      text: 'Action',
      visible: true,
      filterable: false,
      sortable: false,
      datatype: 'action',
      lock: true,
    },
  ],
};

export const ACTIVITY_LOG_EXPORT_MESSAGES = {
  CYCLE_REQUIRED: 'A cycle is required to start an export',
  EXPORT_FAILED: 'Activity log export could not be started',
  FILE_EXPORT_FAILED: 'File export could not be started',
  HISTORY_FAILED: 'Export history could not be loaded',
  DOWNLOAD_FAILED: 'The export file could not be downloaded',
  EXPORT_STARTED: 'Activity log export started',
  FILE_EXPORT_STARTED: 'File export started',
};

export const REQUEST_TYPES = {
  text: 'Text',
  number: 'Number',
  fileupload: 'File Upload',
  link: 'URL Link',
  fileuploadtolink: 'File Upload Link',
  multiselect: 'Multi Select',
  choice: 'Choice',
};

/** Accruals API root. GET calls still go through storeGet so cid is attached. */
export const ACCRUALS_API = '/v2/financialclose/accruals';

/** Information Request channel (tasks UI vs Accruals-generated). */
export const REQUEST_CHANNELS = {
  TASKS: 'tasks',
  ACCRUALS: 'accruals',
};

export const INFORMATION_MANAGER_STAGE_LABEL_MAP = {
  '-1': 'Cancelled',
  '0': 'Pending Submission',
  '1': 'Pending Approval',
  '2': 'Approved',
};

/**
 * Stage values used for filtering requests by status in summary cards
 * Maps to the `stage` field in request line items
 * - null: Show all requests (no filter)
 * - 0: Pending Submission
 * - 1: Pending Approval
 * - 2: Approved
 * - -1: Cancelled
 */
export const INFORMATION_MANAGER_STAGE_VALUES = {
  ALL: null,
  PENDING_SUBMISSION: 0,
  PENDING_APPROVAL: 1,
  APPROVED: 2,
  CANCELLED: -1,
};

/**
 * Maps Information Manager request stage codes to StatusIndicator variants.
 */
export const INFORMATION_MANAGER_STAGE_STATUS_VARIANT = {
  0: 'pending-submission',
  1: 'fc-pending-approval',
  2: 'fc-approved',
  [-1]: 'fc-rejected',
};

export const INFORMATION_MANAGER_TABLE_HEADERS = (stageLabelMap = {}) => ([
  {
    text: 'S No.',
    value: 'index',
    visible: true,
    filterable: false,
    sortable: false,
  },
  {
    text: 'Requested Information',
    value: 'description',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Type',
    value: 'type',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Ref',
    value: 'ref',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Classification',
    value: 'category',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Status',
    value: 'stage',
    visible: true,
    filterable: true,
    sortable: false,
    filterLabelMap: stageLabelMap,
  },
  {
    text: 'Due Days',
    value: 'duedate_days',
    visible: true,
    filterable: true,
    sortable: false,
    type: 'number-range',
  },
  {
    text: 'Requested From',
    value: 'info_owner_name',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Requested By',
    value: 'created_by_name',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Location',
    value: 'location',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Priority',
    value: 'priority',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Created On',
    value: 'created_at',
    visible: true,
    filterable: true,
    sortable: true,
    type: 'date-range',
  },
  {
    text: 'updated On',
    value: 'updated_at',
    visible: false,
    filterable: false,
    sortable: false,
  },
  {
    text: 'Action',
    value: 'action',
    visible: true,
    filterable: false,
    sortable: false,
  },
]);

export const INFORMATION_MANAGER_PRIORITY_LIST = ['High', 'Medium', 'Low'];

export const INFORMATION_MANAGER_TYPE_OPTIONS = [
  {
    name: 'File Upload',
    value: 'fileupload',
    subtitle: 'Owner uploads one or more documents',
    icon: '/img/icons/fbc-icons/ResponseTypeUpload.svg',
    iconSize: 16,
    iconBox: 24,
  },
  {
    name: 'Written Response',
    value: 'text',
    subtitle: 'Owner replies in text — no file',
    icon: '/img/icons/fbc-icons/ResponseTypeMessage.svg',
    iconSize: 20,
    iconBox: 20,
  },
  {
    name: 'Link',
    value: 'link',
    subtitle: 'Shares a URL or hyperlink.',
    icon: '/img/icons/fbc-icons/ResponseTypeLink.svg',
    iconSize: 20,
    iconBox: 20,
  },
];

/** File extension → badge color variant (task detail, information manager file lists). */
export const FILE_BADGE_VARIANTS = {
  PNG: 'pink',
  JPG: 'pink',
  JPEG: 'pink',
  CSV: 'green',
  XLS: 'green',
  XLSX: 'green',
  DOC: 'blue',
  DOCX: 'blue',
  PDF: 'purple',
};

export const FILE_BADGE_VARIANT_DEFAULT = 'default';

/**
 * Constants used throughout the Financial Close module
 */


// Email notification constants
export const EMAIL_NOTIFICATION = {
    FEATURE_TYPES: {
        DASHBOARD: "dashboard",
        TASK_OVERDUE: "taskOverDueNotification",
        OVERDUE: "overdue",
        DUETODAY: "dueToday"
    },
    TYPE: "fbc",
    MAX_EMAIL_INSTANCES: 10,
    REJECTED: "rejected",
    MESSAGES: {
        SCHEDULE_UPDATE_NOTICE: "Please Note: Changes to the notification schedule might take effect after the current scheduled email is sent.",
        MAX_NOTIFICATIONS_ERROR: "Current cycle already have maximum notifications. Cannot add more",
        NOTIFICATION_NOT_ENABLED: "Notification is not enabled. Please contact the Administrator.",
        SUCCESS_CREATE: "Notification created successfully!",
        SUCCESS_UPDATE: "Notification updated successfully!",
        SUCCESS_STATUS_CHANGE: "Notification status changed successfully!",
        FAILED_GET:"Failed to fetch the required details. Please refresh or try again.",
        REQUIRED_VALIDATION: "Please fill all the required fields",
        DUPLICATE_EMAIL: "Duplicate email address is not allowed.",
        LOCATION_ACCESS_DENIED: "You do not have access to every location on this notification.",
        FAILED_UPDATE:"Failed to update notification status",
        CONFIRM_DELETE: "Delete this notification? This cannot be undone.",
        CONFIRM_DELETE_CYCLE: "Remove this notification from the cycle?",
        SUCCESS_DELETE: "Notification deleted",
        FAILED_DELETE: "Unable to delete the notification. Please try again later.",
        CYCLE_CLOSED: "Cycle is closed",
        FEATURE_VALIDATION_MESSAGES:{
            FEATURE_REQUIRED: "Feature is required",
            ROLES_REQUIRED: "Role is required",
            LOCATIONS_REQUIRED: "At least one location must be selected",
            SCHEDULER_TIMING_REQUIRED: "Scheduler Timing is required"
        }
    },
    roles: [
        { name: "Preparer", value: "preparer" },
        { name: "Approver (L1)", value: "approver_l1" },
        { name: "Approver (L2 sign-off)", value: "approver_l2" },
    ],
    /** @deprecated Hour picker on dashboard/tasklist forms; removed with B9. */
    timeList: Array.from({ length: 23 }, (_, i) => ({
        name: `${i + 1} Hour${i + 1 > 1 ? 's' : ''}`,
        value: (i + 1).toString()
    })),
    STATUS:{
        ENABLED: "enable",
        DISABLED: "disable"
    },
    ACTION_TOOLTIP: {
        EDIT: 'Edit',
        DELETE: 'Delete',
        EMAIL_HISTORY: 'Email history',
        ENABLE: 'Enable',
        DISABLE: 'Disable',
    },
    ACTION_ICON: {
        EDIT: '/img/icons/PenEditIcon.svg',
        DELETE: '/img/icons/fbc-icons/DeleteActions.svg',
        EMAIL: '/img/icons/fbc-icons/EmailGreyIcon.svg',
    },
    ALL_ROLES:{
        PREPARER: "preparer",
        APPROVER: "approver",
        SIGN_OFF_APPROVER: "sign_off_approver",
        BOTH: "both"
    },
    expandNotificationRoles(role) {
        if (role === "both") {
            return ["preparer", "approver", "sign_off_approver"];
        }
        if (role === "approver") {
            return ["approver", "sign_off_approver"];
        }
        if (role === "preparer") {
            return ["preparer"];
        }
        if (role === "sign_off_approver") {
            return ["sign_off_approver"];
        }
        return role ? [role] : [];
    },
    collapseNotificationRole(roles) {
        const list = Array.isArray(roles) ? roles : roles ? [roles] : [];
        if (!list.length) return null;
        if (list.includes("both")) return "both";

        const roleSet = new Set(list);
        if (roleSet.has("preparer") && roleSet.has("approver") && roleSet.has("sign_off_approver")) {
            return "both";
        }
        if (roleSet.has("approver") && roleSet.has("sign_off_approver") && !roleSet.has("preparer")) {
            return "approver";
        }
        if (roleSet.has("preparer") && roleSet.size === 1) {
            return "preparer";
        }
        return list[0];
    }
};

export const NOTIFICATION_DEFAULTS = {
  TASK_LIST: {
    endDays: "",
    hour: "",
    category: [],
    emails: "",
    subject: null,
    body: "",
    location: [],
    sla: null,
    priority: null,
  },
  DASHBOARD: {
    endDays: "",
    hour: "",
    emails: "",
    subject: null,
    body: "",
    location: [],
  },
  DUE_TODAY: {
    scheduler: null,
    status: false,
    location: [],
    role: [],
  },
  OVERDUE_TASK: {
    scheduler: null,
    status: false,
    location: [],
    role: [],
  },
};

// Notification type keys for comparison
export const NOTIFICATION_TYPE_KEYS = {
  OVERDUE_TASK: "overdue",
  DUE_TODAY: "dueToday",
  DASHBOARD: "dashboard",
  TASK_LIST_ESCALATION: "taskOverDueNotification",
  TASK_STATUS_REMINDER: "taskStatusReminder",
  UPCOMING_DUE_BY_LOCATION: "upcomingDueByLocation",
  RECON_STATUS_CHANGE: "reconStatusChange",
  TASK_REJECTED: "taskRejected",
};

export const NOTIFICATION_KIND = {
  TEMPLATE: "template",
  SCHEDULE: "schedule",
  TRIGGER: "trigger",
};

export const NOTIFICATION_KIND_LABEL = {
  template: "From template",
  schedule: "Scheduled",
  trigger: "Event",
};

export const NOTIFICATION_KIND_ICON = {
  schedule: "mdi-clock-outline",
  trigger: "mdi-lightning-bolt-outline",
  template: "mdi-content-copy",
};

export const NOTIFICATION_TRIGGER_FILTER = [
  { value: "schedule", label: "Scheduled" },
  { value: "trigger", label: "Event" },
];

export const NOTIFICATION_TEMPLATE_MENU_HEADING = 'Select type';

export const NOTIFICATION_TEMPLATE_MENU = [
  {
    feature: "taskOverDueNotification",
    label: "Escalation",
    description: "Email a lead when tasks breach their SLA",
    icon: "mdi-alert-octagon-outline",
  },
  {
    feature: "dashboard",
    label: "Dashboard digest",
    description: "Email a dashboard summary on a schedule",
    icon: "mdi-view-dashboard-outline",
  },
];

export const NOTIFICATION_FEATURE_LABEL = {
  taskStatusReminder: "Task reminders",
  upcomingDueByLocation: "Upcoming due",
  reconStatusChange: "Recon matched → unmatched",
  taskRejected: "Task rejected",
  dashboard: "Dashboard digest",
  taskOverDueNotification: "Escalation",
  overdue: "Overdue",
  dueToday: "Due today",
};

export const NOTIFICATION_FEATURE_ICON = {
  taskStatusReminder: "mdi-alarm",
  upcomingDueByLocation: "mdi-map-marker-outline",
  reconStatusChange: "mdi-sync",
  taskRejected: "mdi-close-octagon-outline",
  dashboard: "mdi-view-dashboard-outline",
  taskOverDueNotification: "mdi-alert-octagon-outline",
  overdue: "mdi-alarm",
  dueToday: "mdi-alarm",
};

/** Active-schedule notification types (Setup › Notifications tab). */
export const NOTIFICATION_TYPE_FILTER = [
  { value: NOTIFICATION_TYPE_KEYS.TASK_STATUS_REMINDER, label: NOTIFICATION_FEATURE_LABEL.taskStatusReminder },
  { value: NOTIFICATION_TYPE_KEYS.UPCOMING_DUE_BY_LOCATION, label: NOTIFICATION_FEATURE_LABEL.upcomingDueByLocation },
  { value: NOTIFICATION_TYPE_KEYS.RECON_STATUS_CHANGE, label: NOTIFICATION_FEATURE_LABEL.reconStatusChange },
  { value: NOTIFICATION_TYPE_KEYS.TASK_REJECTED, label: NOTIFICATION_FEATURE_LABEL.taskRejected },
];

export const NOTIFICATION_TYPE_ALREADY_CONFIGURED = "Already configured — edit it from the table.";

export const NOTIFICATION_FORM_COPY = {
  NAME_HINT: "Auto-generated from type and locations · edit to customise",
  OFFSET_REQUIRED: "Working days is required",
  OFFSET_WHOLE_NUMBER: "Enter a whole number from 0 to 200. Use Before or After for the direction.",
  NAME_HINT_SIMPLE: "Auto-generated · edit to customise",
  SELECT_TYPE: "Select your type",
  SEARCH_TYPE: "Search notification type",
  KIND_SCHEDULE_HINT: "runs at a set time",
  KIND_EVENT_HINT: "sends when it happens",
  LOCATION: "Location",
  LOCATIONS: "Locations",
  ROLES: "Roles",
  taskStatusReminder: {
    title: "Task reminders",
    subtitle: "Replaces Tasks Overdue, Tasks Due Today, Pending Submission and Pending Approval reminders.",
    offsetLabel: "Due date offset (working days)",
    offsetHint: "Enter a positive number; choose Before or After the due date. 0 = due today and overdue only.",
    rolesHint: "Each person only sees tasks where they hold that role.",
    info: "Every day at the scheduled time, each Preparer and Approver gets one email covering all their open cycles (one table per cycle). A task is listed from N working days before its due date until it is done, with status Pending submission / Pending approval, Due today or Overdue. Up to 200 tasks per email; the rest are linked.",
  },
  upcomingDueByLocation: {
    title: "Upcoming earliest due date by location",
    subtitle: "Heads-up before the first task due date in each location.",
    enableHint: "Applies to cycles created after it is enabled.",
    offsetLabel: "Earliest due date offset (working days)",
    offsetHint: "Positive magnitude only; Before / After relative to earliest due. Default: 2 before.",
    sentTo: "Sent to: Preparers, Approvers, Managers",
    info: "When a cycle is created, the earliest task due date of each location is found and one email per location is scheduled for N working days before it. Due-date changes after cycle creation do not move the email. Each user receives only their tasks.",
  },
  reconStatusChange: {
    title: "Reconciliation matched → unmatched",
    subtitle: "Alerts task owners once when a TB change makes a matched reconciliation unmatched.",
    enableHint: "No schedule: this email is sent by the TB upload / ERP sync itself.",
    flowUpload: "Manual TB upload",
    flowSync: "ERP sync",
    flowChange: "Matched → Unmatched",
    flowEmail: "One email per user per cycle (once)",
    info: "Only the enable switch, locations and roles are configurable. Applies only to Account Reconciliation tasks. Each change is emailed once. Uploading a recon file never sends this email.",
  },
  taskRejected: {
    title: "Task rejected",
    subtitle: "Email when an Approver rejects a task.",
    enableHint: "Turn off to stop rejection emails for this client.",
    sentTo: "Sent to: Preparers of the task",
    trigger: "Trigger: L1 or L2 reject",
    info: "Each Preparer gets one email per rejected task, with the rejection remark and a link to the task. A bulk rejection of several tasks sends one email per task.",
  },
};

export const NOTIFICATION_FEATURE_KIND = {
  taskStatusReminder: "schedule",
  upcomingDueByLocation: "schedule",
  reconStatusChange: "trigger",
  taskRejected: "trigger",
  dashboard: "template",
  taskOverDueNotification: "template",
  overdue: "schedule",
  dueToday: "schedule",
};

export const NOTIFICATION_SETUP_TABS = {
  ACTIVE: "active",
  TEMPLATES: "templates",
  ACTIVITY: "activity",
};

export const NOTIFICATION_SETUP_QUERY = {
  TAB: "tab",
  NOTIFICATION: "notification",
};

export const EMAIL_ACTIVITY_LOG_LEVEL = {
  NONE: "none",
  MINIMAL: "minimal",
  DETAILED: "detailed",
};

/** Run history Source filter — feature keys match notification_activity_logs.metadata.feature */
export const NOTIFICATION_ACTIVITY_SOURCE_OPTIONS = [
  ...NOTIFICATION_TYPE_FILTER,
  {
    value: NOTIFICATION_TYPE_KEYS.DASHBOARD,
    label: NOTIFICATION_FEATURE_LABEL.dashboard,
  },
  {
    value: NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
    label: NOTIFICATION_FEATURE_LABEL.taskOverDueNotification,
  },
];

export const NOTIFICATION_ACTIVITY_OUTCOME_OPTIONS = [
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
];

export const NOTIFICATION_ACTIVITY_CHILD_PREVIEW = 3;

export const NOTIFICATION_SCHEDULE = {
  DEFAULT_FREQUENCY_HOURS: 24,
  DEFAULT_START_TIME: '00:00',
  NEXT_RUNS_COUNT: 5,
  TIME_STEP_MINUTES: 15,
};

export const SETUP_FEATURE_TYPES = new Set([
  NOTIFICATION_TYPE_KEYS.TASK_STATUS_REMINDER,
  NOTIFICATION_TYPE_KEYS.UPCOMING_DUE_BY_LOCATION,
  NOTIFICATION_TYPE_KEYS.RECON_STATUS_CHANGE,
  NOTIFICATION_TYPE_KEYS.TASK_REJECTED,
  NOTIFICATION_TYPE_KEYS.OVERDUE_TASK,
  NOTIFICATION_TYPE_KEYS.DUE_TODAY,
]);

export const NOTIFICATION_TYPES = [
  { name: "Tasks Overdue", value: NOTIFICATION_TYPE_KEYS.OVERDUE_TASK },
  { name: "Tasks Due Today", value: NOTIFICATION_TYPE_KEYS.DUE_TODAY },
  { name: "Dashboard", value: NOTIFICATION_TYPE_KEYS.DASHBOARD },
  {
    name: "Task list Escalation",
    value: NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
  },
];

export const SCHEDULE_CHANGE_NOTICE = EMAIL_NOTIFICATION.MESSAGES.SCHEDULE_UPDATE_NOTICE;

/** Display labels only. ids stay 1 and 2 for the existing escalation payload. */
export const ESCALATION_SLA_OPTIONS = [
  { name: '48 hrs — mail if the due date is crossed by 48 hrs', id: 1 },
  { name: '72 hrs — mail if the due date is crossed by 72 hrs', id: 2 },
];

// Default cycle duration in days
export const DEFAULT_CYCLE_DURATION = 15;

function tableHeader(value, text, extra = {}) {
  return { value, text, sortable: true, filterable: true, visible: true, ...extra };
}

export const dueNotificationHeaders = () => activeScheduleHeaders();

export function activeScheduleHeaders() {
  return [
    tableHeader("name", "Name"),
    tableHeader("type", "Type"),
    tableHeader("trigger", "Trigger"),
    tableHeader("locations", "Locations"),
    tableHeader("recipients", "Recipients"),
    tableHeader("lastRun", "Last run"),
    tableHeader("nextRun", "Next run", { sortable: false }),
    tableHeader("status", "Status"),
    tableHeader("action", "Actions", { sortable: false, filterable: false }),
  ];
}

export function escalationTemplateHeaders() {
  return [
    tableHeader("name", "Name"),
    tableHeader("subject", "Subject"),
    tableHeader("recipients", "Recipients"),
    tableHeader("locations", "Locations"),
    tableHeader("sendsAt", "Sends at"),
    tableHeader("activeWindow", "Active window"),
    tableHeader("status", "Auto-apply"),
    tableHeader("action", "Actions", { sortable: false, filterable: false }),
  ];
}

export function digestTemplateHeaders() {
  return [
    tableHeader("name", "Name"),
    tableHeader("subject", "Subject"),
    tableHeader("recipients", "Recipients"),
    tableHeader("locations", "Locations"),
    tableHeader("sendsAt", "Sends at"),
    tableHeader("activeWindow", "Active window"),
    tableHeader("status", "Auto-apply"),
    tableHeader("action", "Actions", { sortable: false, filterable: false }),
  ];
}

export function cycleEscalationHeaders() {
  return [
    tableHeader("name", "Name"),
    tableHeader("subject", "Subject"),
    tableHeader("recipients", "Recipients"),
    tableHeader("sendsAt", "Sends at"),
    tableHeader("activeWindow", "Active until"),
    tableHeader("status", "On"),
    tableHeader("action", "Actions", { sortable: false, filterable: false }),
  ];
}

export function cycleDigestHeaders() {
  return [
    tableHeader("name", "Name"),
    tableHeader("subject", "Subject"),
    tableHeader("recipients", "Recipients"),
    tableHeader("sendsAt", "Sends at"),
    tableHeader("activeWindow", "Active until"),
    tableHeader("status", "On"),
    tableHeader("action", "Actions", { sortable: false, filterable: false }),
  ];
}

export function notificationActivityHeaders(level) {
  const headers = [
    tableHeader("at", "Time"),
    tableHeader("automation", "Automation"),
    tableHeader("source", "Source"),
    tableHeader("cycle", "Cycle"),
    tableHeader("emails", "Emails"),
    tableHeader("outcome", "Outcome"),
  ];
  if (level === EMAIL_ACTIVITY_LOG_LEVEL.DETAILED) {
    headers.unshift(tableHeader("expand", "", { sortable: false, filterable: false }));
  }
      return headers;
}

/** @deprecated Hour-based cron; cycle/setup now send meta.schedule. Kept for unread legacy rows. */
export const CRON = {
  hourToCronFormat: (hour) => {
    const cronHour = hour === "24" ? "0" : hour;
    return `0 ${cronHour} * * *`;
  },
  cronFormatToHour: (cronStr) => {
    if (typeof cronStr !== "string") return null;
    const parts = cronStr.trim().split(" ");
    if (parts.length < 2) return null;
    let hour = parts[1];
    if (hour === "0") hour = "24";
    return hour ?? "24";
  },
};

export const MESSAGES = {
    INACTIVE_APPROVER_ERROR: "Following approver(s) are inactive users. Please select active users only.",
    INACTIVE_PREPARER_ERROR: "Following preparer(s) are inactive users. Please select active users only.",
    FAILED_TO_GET_USERS: "Failed to fetch users.",
    SELECTED_INACTIVE_USERS:"Selected user is not active. Please remove this user",
    TEAM_MEMBERS_ADDED: (n) => `${n} member${n === 1 ? '' : 's'} added`,
    TEAM_MEMBERS_NONE_ADDED: "No members were added",
    TEAM_SELECT_MEMBERS: "Select at least one member",
    INFO_OWNER_CHANGED: 'Information owner changed',
    USER_ASSOCIATED: 'User Associated',
    USER_CHANGED: 'User Changed',
    USER_REMOVED: 'User Removed',
    USER_REPLACED: 'User Replaced',
};

/**
 * When true, POST all manageuser ops in one request (all-or-nothing batch).
 * When false, call manageuser once per user sequentially.
 */
export const MANAGE_USER_HTTP_BATCH = true;

export const MANAGE_USER_OP = {
  ASSOCIATE: 'associate',
  MOVE: 'move',
  REMOVE: 'remove',
  REPLACE: 'replace',
};

export const TEAM_MEMBER_ROLE = {
  MANAGER: 'manager',
  GENERAL: 'general',
};

// UI Text Constants
export const UI_TEXT = {
  SELECT_ALL: "Select All",
  ALL: "All",
  NA: "NA"
};

export const ACTION_STAGE = {
    ADD_APPROVERS:3,
    ADD_PREPARERS:2,
    REMOVE_USER :"remove",
    MOVE_USER :"move",
    REPLACE_USER :"replace"

}

// API endpoints
export const API_ENDPOINTS = {
  UPDATE_CREATE_SCHEDULER: "/v2/financialclose/analysis/updateOrCreateSchedulerFeature",
  GET_ALL_SCHEDULER: "/v2/financialclose/analysis/getAllSchedulerFeature",
  ADD_TASK_OVERDUE_NOTIFICATION: "/v2/financialclose/analysis/addTaskOverdueNotification",
  EDIT_TASK_OVERDUE_NOTIFICATION: "/v2/financialclose/analysis/editTaskOverdueNotification",

  GET_NOTIFICATION_TYPES: "/v2/financialclose/notification/getAllNotificationsType",
  GET_NOTIFICATIONS_BY_TYPE: "/v2/financialclose/notification/getAllExistingNotifications",
  GET_NOTIFICATION_CONFIG: "/v2/financialclose/notification/getNotificationConfig",
  GET_NOTIFICATION_ACTIVITY_LOGS: "/v2/financialclose/notification/getNotificationActivityLogs",
  DELETE_SETUP_NOTIFICATION: "/v2/financialclose/notification/deleteNotification",
  DELETE_CYCLE_EMAIL_NOTIFICATION: "/v2/financialclose/analysis/deleteSchedulerFeature",
  FC_LOCATION_GET: "/v2/financialclose/location/get",
  CHART_OF_ACCOUNT_GET: "/v2/financialclose/masters/chartofaccount/get",
  CHANGE_CHECKLIST_STAGE: "/v2/financialclose/analysis/changecheckliststage",
  BULK_ADD_TB: "/v2/financialclose/analysis/bulkaddtb",
  TB_UPLOAD_STATUS: "/v3/financialclose/analysis/tbUploadStatus/",
  CREATE_OR_UPDATE_TASK_DUE_NOTIFICATION: "/v2/financialclose/notification/createAndUpdateOverDueAndDueTodayNotification",
  ADD_AND_UPDATE_ESCALATION_AND_DASHBOARD_NOTIFICATION: "/v2/financialclose/notification/addAndUpdateEscalationAndDashboardMasterNotification",
  CHANGE_NOTIFICATION_STATUS: "/v2/financialclose/notification/enableOrDisableNotificationFeature",

  GET_COUNTRYWISE_LOCATION:"/v2/financialclose/location/getcountrywiselocation/",
  GET_DASHBOARD_WITH_FILTERS:"/v2/financialclose/analysis/getdashboardwithfilter/",
  GET_CLIENT:"/v2/client/get/",
  GET_DASHBOARD: "/v2/financialclose/analysis/getdashboard/",
  GET_DEFAULT_DASHBOARD: "/v2/financialclose/analysis/getdefaultdashboard/",
  GET_USERS_FORMDATA: "/v2/financialclose/analysis/getusersformdata",
  GET_DASHBOARD_DETAILS: "/v2/financialclose/analysis/getdetails/",
  MANAGE_CYCLE_USER: "/v2/financialclose/project/manageuser/",

  //  GET_NOTIFICATIONS_BY_TYPE:"/v2/financialclose/analysis/getAllNotifications",
  //  CREATE_OR_UPDATE_TASK_DUE_NOTIFICATION :"/v2/financialclose/analysis/createAndUpdateOverDueAndDueTodayFeature",
  // ADD_AND_UPDATE_ESCALATION_AND_DASHBOARD_NOTIFICATION: "/v2/financialclose/notification/addAndUpdateEscalationAndDashboardMasterNotification",
  // CHANGE_NOTIFICATION_STATUS:"/v2/financialclose/analysis/enableOrDisableNotificationFeature",

  // V3 endpoints
  V3_GET_FORMDATA: "/v3/financialclose/analysis/getformdata",
  V3_GET_DASHBOARD: "/v3/financialclose/analysis/getdashboard/",
  V3_GET_ANALYSIS_DETAILS: "/v3/financialclose/analysis/getdetails/",
  V3_GET_TRIAL_BALANCE: "/v3/financialclose/analysis/gettrialbalance/",
  V3_SAVE_VIEW_CONFIG: "/v3/financialclose/analysis/saveviewconfig",
  V3_EXPORT_TRIAL_BALANCE: "/v3/financialclose/analysis/exporttrialbalance/",
};

export const ERROR_KEYS_CYCLE_TASKLIST = [
  "preparer", "approver", "linkedgl", "description", "duedate",
  "category", "subcategory", "priority", "location", "assertions", "evidences"
];

export const ERROR_KEYS_FC_MASTER_TASKLIST = [
    "preparer", "approver", "glcode", "location", "description",
    "duedays", "category", "subcategory", "priority", "tags", "evidences"
  ];

/**
 * Header value constants for tasklist table columns.
 * Used for value-based lookups instead of hardcoded array indices.
 */
export const HEADER_VALUES = {
  TAGS: 'tags',
  ASSERTIONS: 'assertions',
  NO_OF_EVIDENCE: 'no_of_evidence',
  TB_TOTALS: '__tbtotals',
  SCHEDULE_AMOUNT: 'scheduleAmount',
  EXPLAINED_DIFFERENCE: 'explainedDifference',
  UNEXPLAINED_DIFFERENCE: 'difference',
};

/** Recon amount columns hidden when task-type filter is checklist-only. */
export const RECON_TASKLIST_HEADER_VALUES = [
  HEADER_VALUES.TB_TOTALS,
  HEADER_VALUES.SCHEDULE_AMOUNT,
  HEADER_VALUES.EXPLAINED_DIFFERENCE,
  HEADER_VALUES.UNEXPLAINED_DIFFERENCE,
];

export const CHECKLIST_ONLY_TASK_TYPE_HEADER_VISIBILITY = Object.fromEntries(
  RECON_TASKLIST_HEADER_VALUES.map((value) => [value, false])
);

export const DEFAULT_RECON_TASKLIST_HEADER_VISIBILITY = Object.fromEntries(
  RECON_TASKLIST_HEADER_VALUES.map((value) => [value, true])
);

/**
 * Per-user-type visibility rules for specific tasklist table headers.
 * Maps user type → header value → visible boolean.
 */
export const USER_TYPE_HEADER_VISIBILITY = {
  approver: { [HEADER_VALUES.TAGS]: true, [HEADER_VALUES.NO_OF_EVIDENCE]: false },
  preparer: { [HEADER_VALUES.TAGS]: false, [HEADER_VALUES.NO_OF_EVIDENCE]: true },
  manager: { [HEADER_VALUES.TAGS]: true, [HEADER_VALUES.NO_OF_EVIDENCE]: true },
};


/** Default searchFields for paginated GL account picker (/getglaccounts). */
export const TB_GL_SEARCH_FIELDS = ['glcode', 'name', 'ref', 'locationName'];

/** Column `value` keys for GL account picker tables (TaskGLSelection). */
export const GL_PICKER_HEADER_VALUES = {
  GLCODE: 'glcode',
  NAME: 'name',
  REF: 'ref',
  LOCATION: 'location',
  TYPE: 'type',
  OPENING_AMOUNT: 'opening_amount',
  TRANSACTION: 'transaction',
  AUDIT_ENTRIES: 'audit_entries',
  CLOSING_AMOUNT: 'closing_amount',
  CATEGORY: 'category',
  SUBCATEGORY: 'subcategory',
};

/** Columns that use nowrap cell styling in the GL picker table. */
export const GL_PICKER_NOWRAP_COLUMNS = [
  GL_PICKER_HEADER_VALUES.NAME,
  GL_PICKER_HEADER_VALUES.LOCATION,
  GL_PICKER_HEADER_VALUES.CATEGORY,
  GL_PICKER_HEADER_VALUES.SUBCATEGORY,
];

/** Text columns that show an em dash when empty in the GL picker table. */
export const GL_PICKER_TEXT_FALLBACK_COLUMNS = [
  GL_PICKER_HEADER_VALUES.NAME,
  GL_PICKER_HEADER_VALUES.TYPE,
  GL_PICKER_HEADER_VALUES.CATEGORY,
  GL_PICKER_HEADER_VALUES.SUBCATEGORY,
];

/** Non-sortable headers for GL account picker tables (TaskGLSelection). */
export function getGlPickerTableHeaders(locationHeader = 'Location') {
  return [
    { text: 'GL Code', value: GL_PICKER_HEADER_VALUES.GLCODE, sortable: false, filterable: false, visible: true },
    { text: 'Account Name', value: GL_PICKER_HEADER_VALUES.NAME, sortable: false, filterable: false, visible: true },
    { text: 'Ref', value: GL_PICKER_HEADER_VALUES.REF, sortable: false, filterable: false, visible: true },
    { text: locationHeader, value: GL_PICKER_HEADER_VALUES.LOCATION, sortable: false, filterable: false, visible: true },
    { text: 'Type', value: GL_PICKER_HEADER_VALUES.TYPE, sortable: false, filterable: false, visible: true },
    { text: 'Opening Amount', value: GL_PICKER_HEADER_VALUES.OPENING_AMOUNT, sortable: false, filterable: false, visible: true },
    { text: 'Transaction', value: GL_PICKER_HEADER_VALUES.TRANSACTION, sortable: false, filterable: false, visible: true },
    { text: 'Audit Entries', value: GL_PICKER_HEADER_VALUES.AUDIT_ENTRIES, sortable: false, filterable: false, visible: true },
    { text: 'Closing Amount', value: GL_PICKER_HEADER_VALUES.CLOSING_AMOUNT, sortable: false, filterable: false, visible: true },
    { text: 'Category', value: GL_PICKER_HEADER_VALUES.CATEGORY, sortable: false, filterable: false, visible: true },
    { text: 'Sub Category', value: GL_PICKER_HEADER_VALUES.SUBCATEGORY, sortable: false, filterable: false, visible: true },
  ];
}

/** Numeric columns for linked accounts view tables (drawer / read-only). */
export const LINKED_ACCOUNTS_NUMERIC_COLUMNS = [
  'opening_amount',
  'transaction',
  'audit_entries',
  'closing_amount',
];

/**
 * Read-only table headers for linked accounts view (task detail drawer).
 * GL picker in Add/Edit Task uses getGlPickerTableHeaders (selection + location/ref).
 */
export function getLinkedAccountsViewTableHeaders() {
  return [
    { text: 'Account No', value: 'glcode', visible: true, sortable: false, filterable: false },
    { text: 'Type', value: 'type', visible: true, sortable: false, filterable: false },
    { text: 'Account Name', value: 'name', visible: true, sortable: false, filterable: false },
    { text: 'Category', value: 'category', visible: true, sortable: false, filterable: false },
    { text: 'Sub Category', value: 'subcategory', visible: true, sortable: false, filterable: false },
    { text: 'Opening', value: 'opening_amount', visible: true, sortable: false, filterable: false },
    { text: 'Transaction', value: 'transaction', visible: true, sortable: false, filterable: false },
    { text: 'Journal Entries', value: 'audit_entries', visible: true, sortable: false, filterable: false },
    { text: 'Closing', value: 'closing_amount', visible: true, sortable: false, filterable: false },
  ];
}

// Table Headers values
export function getTasklistTableHeaders(locationHeader = "Location") {
    const headers = [
        {
            text: "Task type",
            value: "task_type",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Task Name",
            value: "description",
            visible: true,
            filterable: true,
            sortable: true,
            linkHover: true,
        },
        {
            text: "Ref",
            value: "ref",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Status",
            value: "stage1",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Action",
            value: "upload",
            visible: true,
            filterable: false,
            sortable: false,
            type: "action",
        },
        {
          text: "TB Amount",
          value: "__tbtotals",
          visible: true,
          filterable: false,
          sortable: false,
        },
        {
            text: "Recon amount",
            value: "scheduleAmount",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Explained difference",
            value: "explainedDifference",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Unexplained difference",
            value: "difference",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Due Date",
            value: "duedate1",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Category",
            value: "category",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Sub category",
            value: "subcategory",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
          text: "Purpose",
          value: "purpose",
          visible: false,
          filterable: true,
          sortable: true,
        },
        {
          text: "Instruction",
          value: "instruction",
          visible: false,
          filterable: true,
          sortable: true,
        },
        {
            text: locationHeader,
            value: "locationName",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Priority",
            value: "priority",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Tags",
            value: "tags",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Assertions",
            value: HEADER_VALUES.ASSERTIONS,
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "No. of evidences",
            value: "no_of_evidence",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Evidence Required",
            value: "evidence_required",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Auto-Approve",
            value: "auto_approve",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Created on",
            value: "created_at",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Preparer",
            value: "preparerids",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Approver",
            value: "approverids",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Information request",
            value: "inforrequest",
            visible: false,
            filterable: false,
            sortable: false,
        },
        {
            text: "Submission date",
            value: "submited_on",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "Completion date",
            value: "closed_date",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "Status detail",
            value: "statusdetail",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "More action",
            value: "action",
            visible: false,
            filterable: false,
            sortable: false,
            type: "action",
            draggable: false,
            class: "more-action-column"
        },
    ];
    return attachApproverPinnedFilterItems(headers);
}

// Task stage constants
export const TASK_STAGES = {
  PENDING_SUBMISSION: 0,
  PENDING_APPROVAL: 2,
  CLOSED: 3
};

/** API workflow_phase values (align with Supernova taskConstants.WORKFLOW_PHASE). */
export const WORKFLOW_PHASE = {
  PENDING_SUBMISSION: 'pending_submission',
  AWAITING_L1: 'awaiting_l1_approval',
  AWAITING_SIGN_OFF: 'awaiting_sign_off',
  CLOSED: 'closed',
};

/** User-facing status labels from API display_status_label. */
export const DISPLAY_STATUS_LABEL = {
  PENDING_SUBMISSION: 'Pending Submission',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  RESUBMISSION: 'Resubmission',
};

export const TASK_EDIT_MESSAGES = {
  APPROVED_LOCKED: 'Approved tasks cannot be edited',
  UPLOAD_LOCKED: 'Bulk upload is only available for pending submission or rejected tasks.',
  UPLOAD_MIXED_SELECTION: 'Deselect tasks that are not pending submission or rejected to use bulk upload.',
  UPLOAD_MIXED_TASK_TYPES:
    'You have chosen both "Account Reconciliation" and "Checklist" task types. Bulk submission is only available for tasks of a single type. Please select tasks of one type to proceed.',
};

/** changecheckliststage API action types (approve | reject | signoff | return_to_pending). */
export const CHECKLIST_STATUS_ACTION = {
  APPROVE: 'approve',
  REJECT: 'reject',
  SIGNOFF: 'signoff',
  RETURN_TO_PENDING: 'return_to_pending',
};

/** L1/L2 rejection remark validation messages */
export const REJECTION_REMARK = {
  REQUIRED: 'Approver remark is required for rejection.',
  INVALID: 'Rejection reason must contain at least one letter or number.',
};

/** Task detail header approve/reject/resubmission reason menus */
export const TASK_DETAIL_REASON_MENU = {
  APPROVE_TITLE: 'Approval',
  REJECT_TITLE: 'Rejection',
  RESUBMISSION_TITLE: 'Request for resubmission',
  SIGN_OFF_TITLE: 'Sign-Off',
  REMARKS_LABEL: 'Remarks',
  REMARKS_REQUIRED_LABEL: 'Remarks*',
  APPROVE_PLACEHOLDER: 'Add a note for the record (optional)',
  REJECT_PLACEHOLDER: 'Explain why this is being rejected...',
  RESUBMISSION_PLACEHOLDER: 'Explain why this is being requested for Resubmission....',
  SIGN_OFF_PLACEHOLDER: 'Add a note for the record (optional)',
  CONFIRM_LABEL: 'Yes, Proceed',
};

export const TASK_STAGE_DISPLAY = {
  [TASK_STAGES.PENDING_SUBMISSION]: {
    class: "pending_sub_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Pending Submission"
  },
  [TASK_STAGES.PENDING_APPROVAL]: {
    class: "pending_app_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Pending Approval"
  },
  [TASK_STAGES.CLOSED]: {
    class: "closed_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Closed "
  }
};

// Task tabs configuration
export const TASK_TABS = [
  {
    key: null,
    label: 'Total Tasks',
    summaryKey: 'count',
    tabClass: 'total_task_tab',
    borderClass: 'total_task-border'
  },
  {
    key: 0,
    label: 'Pending Submissions',
    summaryKey: 'pending',
    tabClass: 'pending_sub_tab',
    borderClass: 'pending-task-border'
  },
  {
    key: 2,
    label: 'Pending Approvals',
    summaryKey: 'pendingapproval',
    tabClass: 'pending_app_tab',
    borderClass: 'approve-task-border'
  },
  {
    key: 3,
    label: 'Approved',
    summaryKey: 'approved',
    tabClass: 'closed_tab',
    borderClass: 'closed-task-border'
  },
  // {
  //   key: -1,
  //   label: 'Rejected',
  //   summaryKey: 'cancelled',
  //   tabClass: 'error-tab',
  //   borderClass: 'error-task-border'
  // }
];

/**
 * Maps close-cycle task stage codes to StatusIndicator variants.
 * Colors align with Tasklist chips and TaskStageTabs indicators
 * (--color-pending-sub-indicator, --color-pending-app-indicator, etc.).
 */
export const TASK_STAGE_STATUS_VARIANT = {
  0: 'pending-submission',
  1: 'fc-pending-approval',
  2: 'fc-pending-approval',
  3: 'fc-approved',
  [-1]: 'fc-rejected',
};

/** Maps task priority labels to CSS modifier keys (e.g. task-card-detail__priority--high). */
export const TASK_PRIORITY_CSS_MODIFIER = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

export const TASK_PRIORITY_CSS_MODIFIER_DEFAULT = 'low';

// Information Manager CSV Upload Expected Headers (cycle bulk)
export const IM_CSV_TASK_TAG_HEADER = 'Task Tag';
export const IM_CSV_LEGACY_TASK_TAG_HEADER = 'Checklist Tag';
export const IM_CSV_TABLE_HEADERS = [
  IM_CSV_TASK_TAG_HEADER, 'Classification', 'Description', 'Info Owner Name', 'Info Owner Email',
  'Type', 'File Upload Link', 'Priority', 'Due Date (dd/mm/yyyy)', 'Location'
];
export const IM_CSV_LEGACY_TABLE_HEADERS = [
  IM_CSV_LEGACY_TASK_TAG_HEADER, 'Classification', 'Description', 'Info Owner Name', 'Info Owner Email',
  'Type', 'File Upload Link', 'Priority', 'Due Date (dd/mm/yyyy)', 'Location'
];

// Information Manager File Upload Types (canonical API codes)
export const IM_FILE_UPLOAD_TYPES = {
  TEXT: 'text',
  FILE_UPLOAD: 'fileupload',
  LINK: 'link',
};

/** Allowed type codes for cycle / master IM create & bulk upload */
export const IM_REQUEST_TYPE_CODES = ['text', 'fileupload', 'link'];

/** Chart of Accounts master writes (`/v2/financialclose/masters/chartofaccount`). */
export const CHART_OF_ACCOUNT_API = {
  ADD: '/v2/financialclose/masters/chartofaccount/add',
  BULK_ADD: '/v2/financialclose/masters/chartofaccount/bulkadd',
  EDIT: (id) => `/v2/financialclose/masters/chartofaccount/edit/${id}`,
};

export const CHART_OF_ACCOUNT_MESSAGES = {
  ADDED: 'New Account added',
  UPDATED: 'Account updated',
  SAVE_FAILED: 'Failed to save account',
};

// Error messages
export const ERROR_MESSAGES = {
  // CSV Upload errors
  NO_FILE_PROVIDED: 'No file provided',
  INVALID_FILE_EXTENSION: 'Only csv file extension supported',
  INVALID_XLSX_FILE_EXTENSION: 'Only xlsx file extension supported',
  INVALID_CSV_HEADERS: (headers) => `Columns '${headers.join(", ")}' are required in the same order`,

  // Validation errors
  DESCRIPTION_REQUIRED: 'Description is required',
  INFO_OWNER_NAME_REQUIRED: 'Info Owner Name is required',
  INFO_OWNER_EMAIL_REQUIRED: 'Info Owner Email is required',
  INFO_OWNER_EMAIL_INVALID: 'Please enter a valid email address',
  CLASSIFICATION_REQUIRED: 'Classification is required',
  DUE_DAYS_NUMBER: 'Due Days must be a number',
  DUE_DAYS_INTEGER: 'Due Days must be a whole number',
  DUE_DAYS_RANGE: 'Due Days must be between -200 and 200',
  DUE_DAYS_REQUIRED: 'Due Days is required',
  REQUESTED_INFORMATION_REQUIRED: 'Requested Information is required',
  REQUESTED_FROM_REQUIRED: 'Requested From is required',
  EMAIL_ID_REQUIRED: 'Email ID is required',
  BULK_UPLOAD_ERROR_FOOTER:
    '*These are fields where errors exist / information missing. Please rectify and re-upload to continue.',
  LOCATION_REQUIRED: 'Location is required',
  INVALID_DUE_DATE: 'Invalid due date. Use dd/mm/yyyy',
  TYPE_REQUIRED: 'Type is required',
  TYPE_INVALID: 'Type must be Written Response, File Upload, or Link',
  COA_ACCOUNT_NO_REQUIRED: 'Account No. is required',
  COA_ACCOUNT_NAME_REQUIRED: 'Account Name is required',
  COA_CATEGORY_REQUIRED: 'Category is required',
  COA_SUB_CATEGORY_REQUIRED: 'Sub Category is required',
  COA_TYPE_INVALID: 'Not a valid type',
  COA_HEADER_MISMATCH: 'Column headers do not match the template. Please re-download the template and try again.',
  CHART_OF_ACCOUNT_UPLOADED: 'Chart of Accounts uploaded successfully',
  ERROR_UPLOADING_CHART_OF_ACCOUNT: 'Error uploading Chart of Accounts',
  UPLOAD_LINK_REQUIRED: 'File Upload Link is required when Type is Link',
  UPLOAD_LINK_INVALID: 'File Upload Link must be a valid link',
  UPLOAD_LINK_NOT_ALLOWED: 'File Upload Link must be empty unless Type is Link',
  TAGS_NOT_FOUND: (tags, location) => `Tag(s) '${tags.join(", ")}' not found in checklist for location: ${location}`,

  // Custom columns errors
  FAILED_GET_CUSTOM_COLUMNS: 'Failed to get custom columns',
  FAILED_FORMAT_CUSTOM_COLUMNS: 'Failed to format custom columns',

  // Date errors
  INVALID_DATE_INPUT: 'Invalid date input',

  // Transaction Matching Reconciliation errors
  ANALYSIS_NOT_RUN: 'Analysis has not yet been run',
  ERROR_GET_COMBO_RESULT: 'Error Get Combo Result',
  ERROR_GET_COMBO_CODE: 'Error Get Combo Code',
  UNKNOWN_ERROR: 'Unknown error!',

  // Information Manager user-facing messages for store/informationManager.js
  INFORMATION_REQUIREMENTS_UPLOADED: 'Information Requirements uploaded successfully',
  ERROR_UPLOADING_INFORMATION_REQUIREMENTS: 'Error uploading Information Requirements',
  PROJECT_ID_REQUIRED: 'Project id is required',
  UNABLE_TO_LOAD_INFORMATION_MANAGER_DETAILS: 'Unable to load Information Manager details',
  NO_PROJECT_FOUND: 'No project found',
  PBC_PROJECT_NOT_FOUND: 'PBC project not found',
  ERROR_FETCHING_REQUEST_DETAILS: 'Error fetching request details',
  ERROR_FETCHING_INFORMATION_OWNER_LIST: 'Error fetching information owner list',
  ERROR_FETCHING_DATA: 'Error fetching data',
  NEW_REQUEST_ADDED: 'New Request Added',
  ERROR_ADDING_REQUEST: 'Error adding request',
  REQUEST_UPDATED: 'Request Updated',
  ERROR_EDITING_REQUEST: 'Error editing request',
  REQUEST_DELETED: 'Request Deleted',
  ERROR_DELETING_REQUEST: 'Error deleting request',
};

// Transaction Matching Reconciliation constants
export const TM_RECONCILIATION = {
  FILE_TYPES: {
    ONE_FILE: 'One File',
    TWO_FILE: 'Two File',
    THREE_FILE: 'Three File',
    SINGLE_FILE: 'Single File',
    MORE: 'More'
  },
  ANALYSIS_TYPES: {
    ACCOUNTS_RECEIVABLE: 'Accounts receivable Reconciliation',
    ACCOUNTS_PAYABLE: 'Accounts payable Reconciliation',
    PG_RECONCILIATION: 'PG Reconciliation (PGR)'
  },
  ANNEXURE_NAMES: {
    // Support both old (with typo) and new (correct spelling) for backward compatibility
    RECONCILIATION_SUMMARY: 'Reconciliation Summary',
    RECONCILIATION_SUMMARY_LEGACY: 'Reconcillation Summary'
  },
  SUMMARY_TYPES: {
    RESULT: 'Result',
    RECONCILIATION: 'Reconciliation'
  },
  VIEW_MODES: {
    GL: 'GL',
    SL: 'SL'
  },
  COMBO_CODE_LABELS: {
    SUMMARY: 'Summary',
    RECONCILIATION: 'Reconciliation'
  },
  COLLECTION_FILTERS: {
    ANNEXURE_SUMMARY: 'annexure_summary',
    ANNEXURE_RECON_SUMMARY: 'annexure_reconSummary'
  },
  EXCLUDED_FILTER: {
    RECONCILIATION: 'Total Excluded',
    AMOUNT: "{'LESS': [], 'ADD': []}"
  },
  NO_DATA_MESSAGE: 'No data available',
  // Group names for reconciliation data grouping
  GROUPS: {
    TABLE_1_TOTAL: 'Table 1 Total',
    EXPLAINED_DIFFERENCE: 'Explained difference',
    UNEXPLAINED_DIFFERENCE: 'Unexplained difference',
    NET_ADJUSTED_AMOUNT: 'Net Adjusted Amount',
    TABLE_2_TOTAL: 'Table 2 Total',
    UNGROUPED: 'Ungrouped'
  },
  // Order for displaying groups (Table 2 Total always last, no grouping needed)
  GROUP_DISPLAY_ORDER: [
    'Table 1 Total',
    'Explained difference',
    'Unexplained difference',
    'Ungrouped',
    'Net Adjusted Amount',
    'Table 2 Total'
  ]
};

// API endpoints for Transaction Matching Reconciliation
export const TM_RECONCILIATION_API = {
  GET_OVERALL_SUMMARY: '/v2/bot/analysis/readcombocodewisereconciliation/overallreconsummary',
  GET_COMBO_SUMMARY: '/v2/bot/analysis/readcombocodewisereconciliation/',
  GET_UNIQUE_COMBO_CODES: '/v2/bot/analysis/getalluniquecombocode/'
};

// Tags list for task remarks and approvals
export const TAGS_LIST = ["#AI"];

// Task upload modal button labels
export const TASK_UPLOAD_LABELS = {
  UPLOAD: 'Upload',
  UPLOAD_AND_SUBMIT: 'Upload & Submit',
  SAVE: 'Save',
  SUBMIT: 'Submit',
};

/** Task detail tab ids shared by the tab strip and refresh guard. */
export const TASK_DETAIL_TAB = {
  SUMMARY: 'summary',
  RECONCILIATION: 'reconciliation',
  FILES_REQUESTS: 'files-requests',
  ACTIVITY: 'activity',
};

/** Task detail fetch / empty-state copy. */
export const TASK_DETAIL_ERRORS = {
  NOT_FOUND: 'Task not found in checklist',
  FETCH_FAILED: 'An error occurred while fetching task details',
};

export const TASK_DETAIL_EMPTY_STATE = {
  NOT_FOUND: {
    TITLE: 'Task not found',
    DESCRIPTION: 'This task is missing from the cycle, or the link is invalid. Return to the task list to continue.',
    ACTION: 'Back to Task List',
  },
  LOAD_ERROR: {
    TITLE: "Couldn't load this task",
    DESCRIPTION: 'Something went wrong while loading task details. Try again, or return to the task list.',
    ACTION: 'Back to Task List',
    RETRY: 'Try again',
  },
};

/** upload_v2.file_type values (mirrors backend processUploads). */
export const TASK_UPLOAD_FILE_TYPES = Object.freeze({
  NORMAL: 'normal',
  RECON: 'recon',
});

/** UI labels keyed by TASK_UPLOAD_FILE_TYPES. */
export const TASK_UPLOAD_FILE_TYPE_LABELS = Object.freeze({
  [TASK_UPLOAD_FILE_TYPES.RECON]: 'RECON',
});

/**
 * AR submit upload mismatch message — keep aligned with backend TASK_SUBMISSION.EVIDENCE_COUNT_MISMATCH_RECONCILIATION.
 * @param {{ evidenceRequired?: number, reconRequired?: number, evidenceFound?: number, reconFound?: number }} counts
 * @returns {string}
 */
export function getAccountReconUploadMismatchMessage({
  evidenceRequired = 0,
  reconRequired = 0,
  evidenceFound = 0,
  reconFound = 0,
} = {}) {
  if (evidenceRequired > 0 && reconRequired === 0) {
    return `You must upload at least ${evidenceRequired} evidence item(s) for this reconciliation task. Found: ${evidenceFound} evidence file(s).`;
  }
  if (evidenceRequired === 0 && reconRequired === 1) {
    return `You must upload exactly one recon file for this reconciliation task. Found: ${reconFound} recon file(s).`;
  }
  return `You must upload at least ${evidenceRequired} evidence item(s) and ${reconRequired} recon file(s) for this reconciliation task. Found: ${evidenceFound} evidence file(s) and ${reconFound} recon file(s).`;
}

export const BULK_UPLOAD_MESSAGES = {
  SENT_FOR_REVIEW: 'Task list sent for review',
  FILES_UPLOADED: 'Files uploaded successfully',
  REMARKS_SAVED: 'Remarks added successfully',
  EVIDENCE_NOT_MATCHED:
    'Selected tasks do not meet the evidence requirements, so they will be saved but not submitted',
};

/** Bulk-edit preview columns that render as an overflow chip list. */
export const BULK_EDIT_OVERFLOW_COLUMNS = Object.freeze([
  'tags',
  'linkedgl',
  'location',
  'assertions',
  'preparer',
  'approver',
  'sign_off_approver',
]);

// Component event names (for $emit)
export const COMPONENT_EVENTS = {
  // Common events
  REFRESH: 'refresh',
  RELOAD: 'reload',
  RESET: 'reset',

  // Update events (v-model related)
  UPDATE_MODEL_VALUE: 'update:modelValue',
  UPDATE_UPLOAD_WITHOUT_FILE: 'update:uploadWithoutFile',
  UPDATE_FILE_REMARK: 'update:fileremark',
  UPDATE_SELECT_GLS: 'update:selectgls',
  UPDATE_SELECTED_LOCATION: 'update:selected-location',
  UPDATE_PREPARER: 'update:preparer',
  UPDATE_APPROVER: 'update:approver',
  UPDATE_SHOW_LOCATION_AND_USER: 'update:showlocationanduser',
  UPDATE_NUMBER_OF_EVIDENCES: 'update:number-of-evidences',

  // Task-related events
  TASK_SUBMITTED: 'task-submitted',
  TASK_UPDATED: 'task-updated',
  TASK_INSTANT_UPDATED: 'task-instant-updated',
  CHECKLIST_ITEM_UPDATED: 'checklist-item-updated',

  // Upload-related events
  BULK_UPLOAD_COMPLETE: 'bulk-upload-complete',
  CLEAR_SELECTED: 'clear-selected',
  FILE_UPLOADED: 'file-uploaded',
  DELETE_FILE: 'delete-file',

  // Navigation/Tab events
  LINKED_GL_TAB: 'linked-gl-tab',
  BACK: 'back',

  // User/Location events
  LOCATION_USERS_SUBMIT: 'location-users-submit',
  USER_TYPE_CHANGE: 'user-type-change',

  // Status change events
  CHANGE_STATUS: 'change-status',

  // Dialog/Modal events
  CLOSE_POPUP: 'close-popup',
  ADD_EDIT_CHECKLIST_ITEM: 'add-edit-checklist-item',
  OPEN_UPLOAD_DRAWER: 'open-upload-drawer',
  OPEN_RECONCILIATION_DIALOGUE: 'open-reconciliation-dialogue',

  // Info request events
  APPROVE: 'approve',
  REJECT: 'reject',
  FOLLOWUP: 'followup',

  // Filter events
  FILTER_CHANGED: 'filter-changed',
  TOGGLE: 'toggle',
  EDIT: 'edit',

  // Loading events
  START_LOADING: 'startloading',
  STOP_LOADING: 'stoploading',

  // Data events
  STORED_DATA: 'storedData',
  INPUT: 'input',

  // Download events
  DOWNLOAD_RESULT: 'download-result'
};

// Legacy task type labels for backward compatibility
export const LEGACY_TASK_TYPE_LABEL_CHECKLIST = 'Checklist';
export const LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME = 'Normal Task';

/** NXTGEN-5442 — unified role model (align with Supernova taskConstants.js) */
export const USER_ROLE = {
  PREPARER: 'preparer',
  APPROVER: 'approver',
  SIGN_OFF_APPROVER: 'sign_off_approver',
  SIGN_OFF_APPROVERS: 'sign_off_approvers',
  MANAGER: 'manager',
};

/** Display labels for assignment roles (bulk drawers, headers). */
export const USER_ROLE_LABEL = {
  PREPARER: 'Preparer',
  APPROVER: 'Approver',
  BULK_SEARCH_PREPARER: 'Search and add Preparer',
  BULK_CURRENT_PREPARERS: 'Current Preparers',
};

export const APPROVER_TAG = {
  L1: 'L1',
  L2: 'L2',
};

/** Sign-Off / two-level approval labels and validation messages (Setup + Cycle) */
export const SIGN_OFF = {
  LABEL: 'Sign-Off',
  APPROVER_LABEL: 'Sign-Off Approver',
  APPROVERS_LABEL: 'Sign-Off Approvers',
  REQUIRES_LABEL: 'Requires Sign-Off',
  REQUIRES_DESC: 'Optional second-level approval',
  REQUIRES_DESC_SETUP: 'Add a second level approver for enhanced task validation.',
  SELECT_APPROVERS_LABEL: 'Select Sign-Off Approvers',
  PICKER_HINT: 'Any one person completing sign-off will advance the task',
  ANY_ONE_OF_LABEL: 'any one of',
  BULK_SEARCH_L1: 'Search and add approver',
  BULK_SEARCH_L2: 'Search and L2 approver',
  BULK_CURRENT_APPROVERS: 'Current Approvers',
  /** Bulk approver editor dialog tab keys */
  EDITOR_TABS: {
    APPROVER: USER_ROLE.APPROVER,
    SIGN_OFF: 'sign_off',
  },
  APPROVER_TAB_LABEL: 'Approver',
  ERRORS: {
    REQUIRED_WHEN_ENABLED: 'Sign-Off Approver is required when Requires Sign-Off is enabled',
    TOGGLE_WITHOUT_ASSIGNEES: 'Sign-Off Approver is required when Requires Sign-Off is enabled',
    ORPHAN_L2: 'Sign-Off Approver cannot be assigned without an Approver',
    PREPARER_SOD: 'Preparer cannot also be Sign-Off Approver',
    APPROVER_SOD: 'Approver cannot also be Sign-Off Approver',
  },
};

export const AUTO_APPROVE = {
  LABEL: 'Auto-Approve On Submission',
  DESC_CHECKLIST: 'Move to Approved once the preparer submits - no manual review step.',
  DESC_AR: 'Approve automatically when the preparer submits with zero unexplained difference against the threshold.',
  SUMMARY_CHIP: 'Auto approval enabled',
  EXCEL_HEADER: 'Auto-Approve',
  FILTER_LABEL: 'Auto Approved',
  FILTER_SUBLABEL: 'Auto-approved tasks',
  FILTER_VALUE: '__auto_approved__',
  TABLE_TAG: 'Auto-approved',
  DETAIL_BADGE: 'Auto-approved',
  SUBMIT_TOAST: 'Task auto-approved',
  ENABLED_BADGE: 'Auto-approval enabled',
  REJECTED_BADGE: 'Auto-approval bypassed',
  REJECTED_TOOLTIP_BODY:
    'This task was previously rejected. It requires manual review and cannot re-enter the auto-approval workflow.',
  RESUBMISSION_BANNER_TITLE: 'Rejected — manual approval now required',
  RESUBMISSION_BANNER_ACTOR_DATE:
    '{actor} rejected this task on {date}, so the auto-approval no longer stands. Auto-approval is switched off for this task from here on. Update the evidence and resubmit, and {actor} will review and approve it manually.',
  RESUBMISSION_BANNER_ACTOR_ONLY:
    '{actor} rejected this task, so the auto-approval no longer stands. Auto-approval is switched off for this task from here on. Update the evidence and resubmit, and {actor} will review and approve it manually.',
  RESUBMISSION_BANNER_DATE_ONLY:
    'This task was rejected on {date}, so the auto-approval no longer stands. Auto-approval is switched off for this task from here on. Update the evidence and resubmit for manual review.',
  RESUBMISSION_BANNER_FALLBACK:
    'This task was rejected, so the auto-approval no longer stands. Auto-approval is switched off for this task from here on. Update the evidence and resubmit for manual review.',
  NEXT_SUBMIT_HINT: 'Applies on next submit.',
  RETURN_TO_PENDING_HINT: 'Auto-approve will not run again after return to Pending Submission.',
};

/** Pinned Approver-column filter row (Auto Approved sentinel). */
export function getApproverColumnPinnedFilterItems() {
  return [
    {
      value: AUTO_APPROVE.FILTER_VALUE,
      label: AUTO_APPROVE.FILTER_LABEL,
      actor: 'system',
      pinned: true,
    },
  ];
}

const TASKLIST_YES_NO_FILTER_COLUMNS = new Set(['evidence_required', 'auto_approve']);
const TASKLIST_YES_NO_FILTER_LABEL_MAP = { true: 'Yes', false: 'No' };

/** Inject Auto Approved pinned filter into approverids header config. */
export function attachApproverPinnedFilterItems(headers = []) {
  if (!Array.isArray(headers)) return headers;
  return headers.map((header) => {
    if (header?.value === 'approverids') {
      return {
        ...header,
        pinnedFilterItems: getApproverColumnPinnedFilterItems(),
      };
    }
    if (TASKLIST_YES_NO_FILTER_COLUMNS.has(header?.value)) {
      return {
        ...header,
        filterLabelMap: TASKLIST_YES_NO_FILTER_LABEL_MAP,
      };
    }
    return header;
  });
}

export const RETURN_TO_PENDING = {
  LABEL: 'Request for Resubmission',
  MENU_TITLE: 'Request Resubmission',
  REASON_LABEL: 'Reason (Required)',
  REMARKS_LABEL: 'Request Resubmission Remarks',
  REMARK_FIELD: 'resubmission_remark',
};

/** True when L1 was closed by system auto-approve (outcome, not config toggle). */
export function isL1AutoApproved(task = {}) {
  return task?.approval_state?.l1_auto_approved === true;
}

export const EVIDENCE_REQUIRED = {
  LABEL: 'Required',
  FIELD: 'evidence_required',
  COUNT_LABEL: 'No of Evidence(s)',
  DESC: 'When required, preparer must attach this many evidence files before submit.',
  EXCEL_HEADER: 'Evidence Required',
  DETAIL_LABEL: 'Evidence required',
  COUNT_MIN: 0,
  COUNT_DEFAULT: 1,
  COUNT_MAX: 10,
  COUNT_REQUIRED_ERROR: 'Enter 1 to 10.',
  COUNT_RANGE_ERROR: 'Enter 0 to 10.',
};

/** True unless the flag is explicitly false. Missing/undefined/null → ON. */
export function isEvidenceRequiredEnabled(value) {
  return value !== false;
}

/**
 * Persist/parse Evidence Required. Only explicit no/false/0 → false.
 * Missing, blank, true, yes, 1 → true.
 * @param {*} value
 * @returns {boolean}
 */
export function coerceEvidenceRequired(value) {
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'no' || normalized === 'false' || normalized === '0') return false;
  }
  return true;
}

// Responsive table height configuration
export const TABLE_OFFSET_SMALL_SCREEN = 415; // Offset for screens < 1080px height
export const TABLE_OFFSET_LARGE_SCREEN = 280; // Offset for screens >= 1080px height
export const LARGE_SCREEN_HEIGHT_THRESHOLD = 1080; // Height threshold to switch offset
export const MS_PER_HOUR = 1000 * 60 * 60;
export const MS_PER_DAY = MS_PER_HOUR * 24;

export const TM_API_ENDPOINTS = {
  GENERATE_RESULT: '/v3/bot/analysis/downloadfile',
  GET_RECENT_DOWNLOADS: '/v2/bot/analysis/getrecentdownloads',
  GET_TM_ANALYSIS_DETAILS: '/v2/bot/analysis/getTransactionMatchingAnalysis',
  TWO_FILES_BULK_MANUAL_MATCH: '/v3/bot/analysis/two-files-bulk-manual-match-consolidated/',
  GET_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/get',
  ADD_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/add',
  UPDATE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/update',
  DELETE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/delete',
  ENABLE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/enable',
  DISABLE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/disable',
  GET_TM_FILTER: '/v2/bot/analysis/tmfilter/get',
  UPSERT_TM_FILTER: '/v2/bot/analysis/tmfilter/upsert',
  DELETE_TM_FILTER: '/v2/bot/analysis/tmfilter/delete',
  ENABLE_TM_FILTER: '/v2/bot/analysis/tmfilter/enable',
  DISABLE_TM_FILTER: '/v2/bot/analysis/tmfilter/disable',
  REORDER_TM_FILTER: '/v2/bot/analysis/tmfilter/reorder',
  GET_INPUT_PARAMETER: '/v2/bot/analysis/inputparameter/get',
  GET_BOT_DETAILS: '/v2/bot/analysis/getbotdetails',
  LOAD_BOT_DETAILS: '/v2/bot/analysis/loadbotdetails',
  ADD_TM_ANALYSIS: '/v2/bot/analysis/addTransactionMatchingAnalysis',
  IMPORT_TM_ANALYSIS: '/v2/bot/analysis/importTransactionMatchingAnalysis',
  RESYNC_TM_ANALYSIS: '/v2/bot/analysis/resyncTransactionMatchingAnalysis',
  DELETE_TM_ANALYSIS: '/v2/bot/analysis/deleteTransactionMatchingAnalysis',
  UPDATE_TM_ANALYSIS: '/v2/bot/analysis/updateTransactionMatchingAnalysis',
  LOAD_TM_ANALYSIS: '/v2/bot/analysis/loadTransactionMatchingAnalysis',
  GET_BOT_ID_BY_NAME: '/v2/bot/analysis/getbotidbyname',
  GET_FORM_DATA: '/v3/bot/analysis/tm/form-data',
  GET_FORM_DATA_V2: '/v2/bot/analysis/tm/getformdata',
  GET_PENDING_PAYOUTS: '/v3/bot/analysis/pending-payouts',
  TM_REPORT_DRILLDOWN: '/v3/bot/analysis/tm/report-drilldown',
  ANNEXURE: '/v3/bot/analysis/annexure',
  ANNEXURE_REPORT_DATES: '/v3/bot/analysis/annexure-report-dates',
  GET_UPLOAD_FILENAMES: '/v2/upload/filenames',
};

/** Pending Payouts identifiers for TM Report tab. */
export const TM_PENDING_PAYOUTS = {
  SECTION: 'Pending Payouts',
  REPORT_ROW_LABEL: 'Pending Payouts to societies',
};

/** Static table names in the TM Report tab (V2). */
export const TM_REPORT_TABLES = {
  BANK_BALANCE_MOVEMENT: 'Bank Balance Movement',
  CLOSING_BALANCE_SUMMARY: 'Closing Balance Summary',
  ORDERED: ['Bank Balance Movement', 'Closing Balance Summary'],
};

/** Excel download / generate-result section identifiers for Transaction Matching */
export const TM_DOWNLOAD_SECTIONS = {
  TM_RESULTS: 'TMResults',
  TM_REPORT_DRILLDOWN: 'TMReportDrillDown',
  PENDING_PAYOUTS: TM_PENDING_PAYOUTS.SECTION,
};

export const TM_MESSAGES = {
  FILE_GENERATING: "File is being generated please try after sometime",
  ERROR_GENERATING: "There was an error while generating file",
  NO_FILE_FOUND: "No file found to download",
  UNKNOWN_ERROR: "Unknown error!",
  DOWNLOAD_IN_PROGRESS: "A download is already in progress. Please wait for it to complete",
  EXPORT_LIMIT_EXCEEDED: (limit) => `Record count exceeds the export limit of ${limit.toLocaleString()} records`,
  EXPORT_LIMIT_EXCEEDED_GENERIC: "Record count exceeds the export limit",
  EXPORT_NO_DATA: 'No records available to export',
  BULK_MANUAL_MATCH_SUCCESS: "Manual matching bulk upload successfully saved",
  NO_FILE_SELECTED: "No file selected or wrong file selected",
  FILE_INGESTION_IN_PROGRESS: "This data source type already has a file being processed. Please wait until it completes before uploading a new file.",
  UPLOAD_IN_PROGRESS: "Upload in progress. Please wait.",
  UPLOAD_FILES_REQUIRED: "Please add at least one file to upload.",
  UPLOAD_FILE_VALIDATION_PENDING: "Please wait while files are being validated.",
  UPLOAD_FILE_VALIDATION_FAILED: "One or more files failed validation. Remove invalid files to proceed.",
};

/** Bulk upload modes for BulkUpload (checklist tasks vs trial balance). */
export const BULK_CHECKLIST_UPLOAD_TYPE = {
  CHECKLIST: 'checklist',
  TRIAL_BALANCE: 'trialbalance',
};

export const UPLOAD_TYPES = {
    NEW_FILE: 'New file',
    // ADD_TO_EXISTING: 'Add to existing',
    // REPLACE_EXISTING: 'Replace existing',
    REPLACE_OR_ADD: 'Replace or add to existing',
    DELETE_FILE: 'Delete file',
};

/** Options shown when no file exists yet for a given file type */
export const UPLOAD_TYPE_OPTIONS_NEW = [UPLOAD_TYPES.NEW_FILE];

/** Options shown when one or more files already exist for a given file type */
export const UPLOAD_TYPE_OPTIONS_EXISTING = [
    // UPLOAD_TYPES.ADD_TO_EXISTING,
    // UPLOAD_TYPES.REPLACE_EXISTING,
    UPLOAD_TYPES.REPLACE_OR_ADD,
];

export const FILE_STAGES = {
    SUCCESSFUL: 1,
    QUEUED: 16,
    PROCESSING: 17,
    FAILED: -1,
    DELETED: -2,
};

/** Default stale threshold for in-progress file ingestion (minutes). Overridden by tm/form-data `fileingestionTimeout`. */
export const DEFAULT_FILE_INGESTION_TIMEOUT_MINUTES = 30;

export const isFileIngestionInProgress = (file) => {
    if (!file) return false;
    const stages = [file.stage, file.processed];
    return stages.some(
        s => s === FILE_STAGES.QUEUED || s === FILE_STAGES.PROCESSING
    );
};

/**
 * True when a file is in-progress and younger than the stale threshold.
 * Ingestion older than the threshold is treated as stale and does not block new uploads.
 */
export const isActiveFileIngestion = (file, thresholdMs) => {
    if (!isFileIngestionInProgress(file)) return false;
    const ts = file.createdAt || file.uploadedAt || file.updatedAt;
    if (!ts) return true;
    return (Date.now() - new Date(ts).getTime()) < thresholdMs;
};

export const DOWNLOAD_STAGES = {
    QUEUED: 0,
    READY: 1,
    FAILED: 2,
    PROCESSING: 4,
    FILE_QUEUED: 16,
    FILE_PROCESSING: 17,
};

export const isDownloadInProgress = (stage) =>
    stage === DOWNLOAD_STAGES.QUEUED || stage === DOWNLOAD_STAGES.PROCESSING ||
    stage === DOWNLOAD_STAGES.FILE_QUEUED || stage === DOWNLOAD_STAGES.FILE_PROCESSING;

export const isDownloadFailed = (stage) => stage === DOWNLOAD_STAGES.FAILED;

export const isDownloadReady = (stage) => !isDownloadInProgress(stage) && !isDownloadFailed(stage);

export const TM_STATUS = {
    NOT_STARTED: 0,
    FINISHED: 1,
    FAILED: 2,
    IN_PROGRESS: 4,
    QUEUED: 5,
    AI_IN_PROGRESS: 9,
    MANUAL_MATCHING_IN_PROGRESS: 12,
    MANUAL_MATCHING_PROGRESS: 13,
};

export const TM_ACTION_STAGES = {
    QUEUED: 1,
    IN_PROGRESS: 2,
    COMPLETED: 3,
    FAILED: 4,
};

/** Action types whose queue status can override run status in TM config UI */
export const TM_STATUS_TRACKED_ACTIONS = [
    'import_analysis_data',
    'resync_analysis_data',
];

/** Fields kept on a selected dependency chip so a reload can restore the picker row. */
export const DEPENDENCY_PICKER_DISPLAY_KEYS = [
  'ref',
  'description',
  'duedays',
  'duedate',
  'locationName',
];

/** Setup Task Library — template dependency editor (NXTGEN-6833 / 7389). */
export const SETUP_TEMPLATE_DEPENDENCY = {
  MODE: 'setup',
  LINK_TYPE_BLOCKS: 'blocks',
  LINK_TYPE_BLOCKING: 'blocking',
  LINK_TYPE_RELATED: 'related',
  LINK_TYPE_OPTIONS: [
    { label: 'Blocks', value: 'blocks' },
    { label: 'Waiting on', value: 'blocking' },
    { label: 'Related', value: 'related' },
  ],
  PICKER_LINK_TYPE_OPTIONS: [
    { label: 'Blocks', value: 'blocks' },
    { label: 'Related', value: 'related' },
  ],
  EMPTY_DIALOG_HELPER:
    'Tick the tasks to link, then set how each one relates to this task.',
  EMPTY_DIALOG_SUMMARY: 'No dependencies linked',
  EMPTY_LINKED_TITLE: 'No tasks linked yet',
  EMPTY_BLOCKING_BODY: 'This task is not currently blocking any other tasks.',
  PICKER_SEARCH_PLACEHOLDER: 'Search task by name, reference, or location',
  PICKER_DUE_REQUIRED: 'Set due days on this template before linking dependencies.',
  PICKER_EMPTY: 'No matching templates',
  COL_TASK_DETAILS: 'Task details',
  COL_DUE: 'Due',
  COL_LOCATION: 'Location',
  COL_LINK_TYPE: 'Link type',
  DETAIL_COL_LINK_TYPE: 'Link type',
  DETAIL_COL_REF: 'Ref',
  DETAIL_COL_TASK_NAME: 'Task name',
  DETAIL_COL_DUE_DAYS: 'Due days',
  DETAIL_EMPTY: 'No dependencies linked to this template.',
  DETAIL_EMPTY_TITLE: 'No dependencies',
  DETAIL_EMPTY_BODY: 'Linked tasks will appear here.',
  DETAIL_WAITING_ON: 'Waiting on',
  DETAIL_BLOCKING: 'Blocking',
  DETAIL_RELATED: 'Related',
  SAVE_LINK_TYPE_REQUIRED: 'Choose a link type for each selected task.',
  PICKER_DISABLED_TOOLTIP_TITLE: "Can't be linked",
  PICKER_DISABLED_REASON_MESSAGES: {
    DUE_ORDER: 'A blocking task cannot have a due date on or later than the task it is linked',
    CIRCULAR:
      'This dependency cannot be created because it would create a circular dependency between tasks.',
    MISSING_DUE: 'This task has no due date and cannot be linked as a dependency.',
  },
  PICKER_DISABLED_TOOLTIP_FALLBACK: 'This task cannot be linked.',
  DUE_BEFORE_BLOCKED_DAYS: (days) => `Choose due days less than ${days}.`,
};

/**
 * Location master `type` values.
 * Aligns with supernova `LOCATION_MASTER_TYPE` (NXTGEN-7862).
 */
export const LOCATION_MASTER_TYPE = {
  CONSOLIDATED: 'Consolidated',
};

/** Assign Accounts copy in setup and cycle AR pickers (NXTGEN-7862). */
export const ASSIGN_ACCOUNTS_HINT = {
  FILTERED_BY_LOCATIONS:
    'The available accounts below are automatically filtered based on the selected locations.',
  FILTERED_BY_LOCATION:
    'The available accounts below are automatically filtered based on the selected location.',
  CONSOLIDATED: 'Consolidated locations can use accounts from any location.',
  EMPTY_LOCATION: 'No accounts found for this location',
  EMPTY: 'No accounts found',
  EMPTY_SEARCH: 'No accounts match your search',
};

export const ASSIGNMENT_LOCATION_TYPE = {
  MIXED_ERROR:
    'Consolidated and non-consolidated locations cannot be selected in the same assignment.',
};

/** Cycle task dependencies (NXTGEN-6833). */
export const CYCLE_TASK_DEPENDENCY = {
  MODE: 'cycle',
  DETAIL_FIELD: {
    BLOCKING: 'blocking',
    BLOCKED_BY: 'blockedBy',
    RELATES: 'relates',
    CAN_APPROVE: 'canApprove',
  },
  DETAIL_WAITING_ON: 'Waiting on',
  DETAIL_BLOCKING: 'Blocks',
  DETAIL_RELATED: 'Related',
  DETAIL_STATUS_NOT_VISIBLE: 'Status not visible to you',
  LIST_BLOCKED: 'Blocked',
  EMPTY: 'No dependencies linked to this task.',
  DETAIL_EMPTY_TITLE: 'No dependencies',
  DETAIL_EMPTY_BODY: 'Linked tasks will appear here.',
  LINK_TYPE_BLOCKS: 'blocks',
  LINK_TYPE_RELATED: 'related',
  LINK_TYPE_OPTIONS: [
    { label: 'Blocks', value: 'blocks' },
    { label: 'Related', value: 'related' },
  ],
  EMPTY_DIALOG_HELPER: 'Tick the tasks to link, then set how each one relates to this task.',
  EMPTY_DIALOG_SUMMARY: 'No dependencies linked',
  EMPTY_LINKED_TITLE: 'No tasks linked yet',
  EMPTY_BLOCKING_BODY: 'This task is not currently blocking any other tasks.',
  PICKER_SEARCH_PLACEHOLDER: 'Search task by name, reference, or location',
  PICKER_DUE_REQUIRED: 'Set a due date on this task before linking dependencies.',
  PICKER_EMPTY: 'No matching tasks',
  PICKER_WAITING_ON_TOOLTIP_TITLE: 'Waiting on',
  PICKER_WAITING_ON_TOOLTIP_BODY:
    'This task is blocking the task you are editing. It cannot be changed here.',
  SAVE_LINK_TYPE_REQUIRED: 'Choose a link type for each selected task.',
  COL_TASK_DETAILS: 'Task details',
  COL_DUE: 'Due',
  COL_STATUS: 'Status',
  COL_LOCATION: 'Location',
  COL_LINK_TYPE: 'Link type',
  DUE_DATE_FORMAT: 'DD MMM YYYY',
  APPROVAL_UNAVAILABLE_TITLE: 'Approval unavailable',
  REJECT_UNAVAILABLE_TITLE: 'Reject unavailable',
  RESUBMISSION_UNAVAILABLE_TITLE: 'Request for resubmission unavailable',
  RESUBMISSION_BLOCKED_BY_ONE: 'Blocking 1 approved task',
  RESUBMISSION_BLOCKED_BY_MANY: 'Blocking {count} approved tasks',
  RESUBMISSION_BLOCKED_FOOTER:
    'Request for resubmission is unavailable while a task this one blocks is approved.',
  resubmissionBlockedMessage: (refs) => {
    const list = (Array.isArray(refs) ? refs : [])
      .map((ref) => String(ref || '').trim())
      .filter(Boolean);
    if (list.length === 1) {
      return `Request for resubmission is unavailable because ${list[0]}, a task this one blocks, is already approved.`;
    }
    const label = list.length ? list.join(', ') : 'tasks this one blocks';
    return `Request for resubmission is unavailable because these tasks it blocks are already approved: ${label}.`;
  },
  APPROVAL_BLOCKED_BY_ONE: 'Blocked by 1 task',
  APPROVAL_BLOCKED_BY_MANY: 'Blocked by {count} tasks',
  APPROVAL_UNLOCKS_ONCE_ONE: 'Unlocks once it is approved',
  APPROVAL_UNLOCKS_ONCE_BOTH: 'Unlocks once both are approved',
  APPROVAL_UNLOCKS_ONCE_ALL: 'Unlocks once all are approved',
  DUE_BEFORE_BLOCKED_DATE: (label) => `Choose a due date before ${label}.`,
  CALENDAR_BLOCKED_DUE_TOOLTIP: "Date must be on or after the dependent task's due date",
};

